// Generated from packages/schema/scenix.schema.json. Do not edit.
//
// That schema is the single definition of every shape crossing the API
// boundary. This file is one of its two outputs; Go is verified against the
// same document (ADR 000020). Regenerate with `mise run schema:generate`.
import { z } from "zod";
export const UserIdSchema = z
    .string()
    .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
    .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U.");
export const SpaceIdSchema = z
    .string()
    .regex(new RegExp("^spc_[0-9A-Za-z]{11}$"))
    .describe("A space identifier. Eleven letters and digits, no separators, because it appears in an address somebody reads and types (ADR 000033).");
export const ProjectIdSchema = z
    .string()
    .regex(new RegExp("^prj_[0-9A-Za-z]{11}$"))
    .describe("A project identifier. Eleven letters and digits, no separators (ADR 000033).");
export const DocumentIdSchema = z.string().regex(new RegExp("^doc_[0-9A-HJKMNP-TV-Z]{26}$"));
export const AssetIdSchema = z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$"));
export const ThreadIdSchema = z.string().regex(new RegExp("^thr_[0-9A-HJKMNP-TV-Z]{26}$"));
export const MessageIdSchema = z.string().regex(new RegExp("^msg_[0-9A-HJKMNP-TV-Z]{26}$"));
export const QuestionIdSchema = z.string().regex(new RegExp("^qst_[0-9A-HJKMNP-TV-Z]{26}$"));
export const OptionalDocumentIdSchema = z
    .string()
    .regex(new RegExp("^(doc_[0-9A-HJKMNP-TV-Z]{26})?$"))
    .describe('A document id or the empty string. Go marshals a zero identifier as "" rather than omitting it, because omitempty does not apply to struct types.');
export const OptionalUserIdSchema = z.string().regex(new RegExp("^(usr_[0-9A-HJKMNP-TV-Z]{26})?$"));
export const OptionalQuestionIdSchema = z
    .string()
    .regex(new RegExp("^(qst_[0-9A-HJKMNP-TV-Z]{26})?$"));
export const SlugSchema = z
    .string()
    .regex(new RegExp("^[a-z0-9]+(-[a-z0-9]+)*$"))
    .min(2)
    .max(64)
    .describe("Lowercase kebab-case, as the address of a space or project.");
export const TimestampSchema = z.string().datetime({ offset: true });
export const Sha256Schema = z.string().regex(new RegExp("^[0-9a-f]{64}$"));
export const SpaceRoleSchema = z
    .enum(["viewer", "editor", "admin", "owner"])
    .describe("Ordered least to most authority; the order is load-bearing.");
export const MembershipVisibilitySchema = z
    .enum(["all", "listed"])
    .describe("What a membership may see, orthogonal to its role. All by default (ADR 000046).");
export const MembershipStatusSchema = z
    .enum(["active", "inactive"])
    .describe("Whether a membership currently grants anything. A membership is never deleted: somebody who leaves keeps their record and reactivating returns the same one (ADR 000059).");
export const ProjectFormatSchema = z.enum(["short", "long_form", "film", "series_episode"]);
export const ProjectStatusSchema = z.enum(["draft", "active", "archived"]);
export const DocumentKindSchema = z.enum(["treatment", "script", "outline", "note", "shot_list"]);
export const AssetKindSchema = z
    .enum(["video", "image", "audio", "document", "link"])
    .describe("What scenix ingests. `document` is a file of text (a PDF, a transcript, notes) and is not a Document, which is the Markdown a project edits (ADR 000028).");
export const AssetSourceSchema = z
    .enum(["upload", "youtube", "link", "generated"])
    .describe("How an asset entered the system.");
export const YouTubeDomainSchema = z
    .enum(["youtube.com", "youtu.be", "youtube-nocookie.com"])
    .describe("The domains YouTube serves from. A host matches one exactly or sits beneath it on a dot boundary (ADR 000122). Defined here although no field carries it, because it decides what a client may send as a youtube import: a surface that answers the question differently sends an address the API refuses, or sends source: link for one the API would have imported as a video (ADR 000124).");
export const AssetStatusSchema = z
    .enum(["pending", "analyzing", "ready", "failed"])
    .describe("Where an asset is in ingest: pending -> analyzing -> ready | failed. A pending asset past its uploadExpiresAt is abandoned and cannot reach another state (ADR 000054).");
export const InviteStatusSchema = z
    .enum(["pending", "accepted", "redundant", "revoked", "expired"])
    .describe("Where an invitation got to. Accepted means it produced a membership; redundant means it reached somebody already in the space and granted nothing. Both are spent, and both keep letting that address sign in until the invite is revoked (ADR 000098).");
export const AuthorKindSchema = z.enum(["user", "assistant", "system"]);
export const QuestionStatusSchema = z.enum([
    "open",
    "answered",
    "needs_human",
    "resolved",
    "dismissed",
]);
export const TimeRangeSchema = z
    .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
    .strict()
    .describe("A half-open selection over an asset's timeline, in seconds.");
export const PublicUserSchema = z
    .object({
    id: z
        .string()
        .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
    email: z.string().email(),
    displayName: z.string().min(1).max(200),
    avatarUrl: z
        .union([
        z.string().describe("Always present, null when there is no avatar."),
        z.null().describe("Always present, null when there is no avatar."),
    ])
        .describe("Always present, null when there is no avatar."),
})
    .strict()
    .describe("The projection safe to expose to other members of a space. The Google subject is deliberately absent.");
export const SpaceSchema = z
    .object({
    id: z
        .string()
        .regex(new RegExp("^spc_[0-9A-Za-z]{11}$"))
        .describe("A space identifier. Eleven letters and digits, no separators, because it appears in an address somebody reads and types (ADR 000033)."),
    slug: z
        .string()
        .regex(new RegExp("^[a-z0-9]+(-[a-z0-9]+)*$"))
        .min(2)
        .max(64)
        .describe("Lowercase kebab-case, as the address of a space or project."),
    name: z.string().min(1).max(200),
    createdBy: z
        .string()
        .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
    createdAt: z.string().datetime({ offset: true }),
    logoPath: z
        .string()
        .describe("Where the logo lives in the object store. Empty until one is uploaded; not a URL, since the browser reaches it through the logo endpoint rather than the store directly."),
    deletedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .describe("Set when the space is in the recovery window: it and everything beneath it are still here and unreachable, until it is restored or swept (ADR 000062)."),
})
    .strict();
export const SpaceWithRoleSchema = z
    .object({
    id: z
        .string()
        .regex(new RegExp("^spc_[0-9A-Za-z]{11}$"))
        .describe("A space identifier. Eleven letters and digits, no separators, because it appears in an address somebody reads and types (ADR 000033)."),
    slug: z
        .string()
        .regex(new RegExp("^[a-z0-9]+(-[a-z0-9]+)*$"))
        .min(2)
        .max(64)
        .describe("Lowercase kebab-case, as the address of a space or project."),
    name: z.string().min(1).max(200),
    createdBy: z
        .string()
        .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
    createdAt: z.string().datetime({ offset: true }),
    logoPath: z
        .string()
        .describe("Where the logo lives in the object store. Empty until one is uploaded; not a URL, since the browser reaches it through the logo endpoint rather than the store directly."),
    deletedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .describe("Set when the space is in the recovery window: it and everything beneath it are still here and unreachable, until it is restored or swept (ADR 000062)."),
    role: z
        .enum(["viewer", "editor", "admin", "owner"])
        .describe("Ordered least to most authority; the order is load-bearing."),
})
    .strict()
    .describe("A space as one member sees it: the space, plus what they may do in it.");
export const MemberSchema = z
    .object({
    user: z
        .object({
        id: z
            .string()
            .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
            .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
        email: z.string().email(),
        displayName: z.string().min(1).max(200),
        avatarUrl: z
            .union([
            z.string().describe("Always present, null when there is no avatar."),
            z.null().describe("Always present, null when there is no avatar."),
        ])
            .describe("Always present, null when there is no avatar."),
    })
        .strict()
        .describe("The projection safe to expose to other members of a space. The Google subject is deliberately absent."),
    role: z
        .enum(["viewer", "editor", "admin", "owner"])
        .describe("Ordered least to most authority; the order is load-bearing."),
    visibility: z
        .enum(["all", "listed"])
        .describe("What a membership may see, orthogonal to its role. All by default (ADR 000046)."),
    visibleProjectIds: z
        .array(z
        .string()
        .regex(new RegExp("^prj_[0-9A-Za-z]{11}$"))
        .describe("A project identifier. Eleven letters and digits, no separators (ADR 000033)."))
        .describe("Meaningful only when visibility is listed; empty otherwise."),
    status: z
        .enum(["active", "inactive"])
        .describe("Whether a membership currently grants anything. A membership is never deleted: somebody who leaves keeps their record and reactivating returns the same one (ADR 000059)."),
    deactivatedAt: z
        .union([
        z.string().datetime({ offset: true }).describe("When they stopped. Null while active."),
        z.null().describe("When they stopped. Null while active."),
    ])
        .describe("When they stopped. Null while active.")
        .optional(),
})
    .strict();
export const ChainProgressSchema = z
    .object({
    plannedSec: z
        .number()
        .gte(0)
        .describe("Where the chain ends: the planned length, which is what the briefs say. Zero when nothing has been planned."),
    madeSec: z.number().gte(0).describe("How much of that length has a picture."),
})
    .strict()
    .describe("How far a project's chain has actually been taken, read from its timeline rather than declared by anybody (ADR 000067). A fraction and a duration rather than one of six nouns, because a long video is routinely part cut and part storyboard.");
export const ProjectSchema = z
    .object({
    id: z
        .string()
        .regex(new RegExp("^prj_[0-9A-Za-z]{11}$"))
        .describe("A project identifier. Eleven letters and digits, no separators (ADR 000033)."),
    spaceId: z
        .string()
        .regex(new RegExp("^spc_[0-9A-Za-z]{11}$"))
        .describe("A space identifier. Eleven letters and digits, no separators, because it appears in an address somebody reads and types (ADR 000033)."),
    slug: z
        .string()
        .regex(new RegExp("^[a-z0-9]+(-[a-z0-9]+)*$"))
        .min(2)
        .max(64)
        .describe("Lowercase kebab-case, as the address of a space or project."),
    name: z.string().min(1).max(300),
    format: z.enum(["short", "long_form", "film", "series_episode"]),
    status: z.enum(["draft", "active", "archived"]),
    treatmentId: z
        .string()
        .regex(new RegExp("^(doc_[0-9A-HJKMNP-TV-Z]{26})?$"))
        .describe('A document id or the empty string. Go marshals a zero identifier as "" rather than omitting it, because omitempty does not apply to struct types.'),
    createdBy: z
        .string()
        .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    stage: z
        .enum(["treatment", "script", "animatic", "rough_cut", "fine_cut", "master"])
        .describe("How far the work has been taken. Terms of art: an animatic is stills with timing and audio, a storyboard is static panels (ADR 000021)."),
    chain: z
        .object({
        plannedSec: z
            .number()
            .gte(0)
            .describe("Where the chain ends: the planned length, which is what the briefs say. Zero when nothing has been planned."),
        madeSec: z.number().gte(0).describe("How much of that length has a picture."),
    })
        .strict()
        .describe("How far a project's chain has actually been taken, read from its timeline rather than declared by anybody (ADR 000067). A fraction and a duration rather than one of six nouns, because a long video is routinely part cut and part storyboard."),
    deletedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .describe("Set when the project is in the recovery window: it and everything beneath it are still here and unreachable, until it is restored or swept (ADR 000078)."),
})
    .strict();
export const DocumentSchema = z
    .object({
    id: z.string().regex(new RegExp("^doc_[0-9A-HJKMNP-TV-Z]{26}$")),
    projectId: z
        .string()
        .regex(new RegExp("^prj_[0-9A-Za-z]{11}$"))
        .describe("A project identifier. Eleven letters and digits, no separators (ADR 000033)."),
    kind: z.enum(["treatment", "script", "outline", "note", "shot_list"]),
    title: z.string().min(1).max(300),
    body: z.string(),
    revision: z.number().int().gte(1),
    createdBy: z
        .string()
        .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
    updatedBy: z
        .string()
        .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    deletedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .describe("Set when the document is in the recovery window: the body and the revision are both still here, invisible until restored (ADR 000076)."),
})
    .strict()
    .describe("CommonMark. The revision is a monotonic counter; a stale one is rejected on write.");
export const NewSpaceSchema = z
    .object({
    name: z.string().min(1).max(200),
    slug: z
        .string()
        .regex(new RegExp("^[a-z0-9]+(-[a-z0-9]+)*$"))
        .min(2)
        .max(64)
        .describe("Lowercase kebab-case, as the address of a space or project.")
        .optional(),
})
    .strict()
    .describe("Write payload. The server owns the id, the creator and the timestamps.");
export const NewProjectSchema = z
    .object({
    name: z.string().min(1).max(300),
    format: z.enum(["short", "long_form", "film", "series_episode"]).optional(),
    slug: z
        .string()
        .regex(new RegExp("^[a-z0-9]+(-[a-z0-9]+)*$"))
        .min(2)
        .max(64)
        .describe("Lowercase kebab-case, as the address of a space or project.")
        .optional(),
    stage: z
        .enum(["treatment", "script", "animatic", "rough_cut", "fine_cut", "master"])
        .describe("How far the work has been taken. Terms of art: an animatic is stills with timing and audio, a storyboard is static panels (ADR 000021).")
        .optional(),
})
    .strict();
export const ProblemSchema = z
    .object({
    type: z.string(),
    title: z.string(),
    status: z.number().int().gte(100).lte(599),
    detail: z.string().optional(),
})
    .strict()
    .describe("RFC 9457 problem details: the single error shape every endpoint returns.");
export const UserSchema = z
    .object({
    id: z
        .string()
        .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
    email: z.string().email(),
    displayName: z.string().min(1).max(200),
    avatarUrl: z.string().optional(),
    createdAt: z.string().datetime({ offset: true }),
    lastSeenAt: z.string().datetime({ offset: true }).optional(),
})
    .strict()
    .describe("A person. The Google subject is never serialised: it is the identity, and it is nobody's business but the server's.");
export const MembershipSchema = z
    .object({
    id: z.string().regex(new RegExp("^mem_[0-9A-HJKMNP-TV-Z]{26}$")),
    spaceId: z
        .string()
        .regex(new RegExp("^spc_[0-9A-Za-z]{11}$"))
        .describe("A space identifier. Eleven letters and digits, no separators, because it appears in an address somebody reads and types (ADR 000033)."),
    userId: z
        .string()
        .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
    role: z
        .enum(["viewer", "editor", "admin", "owner"])
        .describe("Ordered least to most authority; the order is load-bearing."),
    visibility: z
        .enum(["all", "listed"])
        .describe("What a membership may see, orthogonal to its role. All by default (ADR 000046)."),
    visibleProjectIds: z
        .array(z
        .string()
        .regex(new RegExp("^prj_[0-9A-Za-z]{11}$"))
        .describe("A project identifier. Eleven letters and digits, no separators (ADR 000033)."))
        .describe("Meaningful only when visibility is listed; empty otherwise."),
    createdAt: z.string().datetime({ offset: true }),
    status: z
        .enum(["active", "inactive"])
        .describe("Whether a membership currently grants anything. A membership is never deleted: somebody who leaves keeps their record and reactivating returns the same one (ADR 000059)."),
    deactivatedAt: z
        .union([
        z.string().datetime({ offset: true }).describe("When they stopped. Null while active."),
        z.null().describe("When they stopped. Null while active."),
    ])
        .describe("When they stopped. Null while active.")
        .optional(),
})
    .strict();
export const InviteSchema = z
    .object({
    id: z.string().regex(new RegExp("^inv_[0-9A-HJKMNP-TV-Z]{26}$")),
    spaceId: z
        .string()
        .regex(new RegExp("^spc_[0-9A-Za-z]{11}$"))
        .describe("A space identifier. Eleven letters and digits, no separators, because it appears in an address somebody reads and types (ADR 000033)."),
    email: z.string().email(),
    role: z
        .enum(["viewer", "editor", "admin", "owner"])
        .describe("Ordered least to most authority; the order is load-bearing."),
    visibility: z
        .enum(["all", "listed"])
        .describe("What a membership may see, orthogonal to its role. All by default (ADR 000046)."),
    visibleProjectIds: z
        .array(z
        .string()
        .regex(new RegExp("^prj_[0-9A-Za-z]{11}$"))
        .describe("A project identifier. Eleven letters and digits, no separators (ADR 000033)."))
        .describe("Meaningful only when visibility is listed; empty otherwise."),
    status: z
        .enum(["pending", "accepted", "redundant", "revoked", "expired"])
        .describe("Where an invitation got to. Accepted means it produced a membership; redundant means it reached somebody already in the space and granted nothing. Both are spent, and both keep letting that address sign in until the invite is revoked (ADR 000098)."),
    invitedBy: z
        .string()
        .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
    createdAt: z.string().datetime({ offset: true }),
    expiresAt: z.string().datetime({ offset: true }),
})
    .strict()
    .describe("Addressed to an email rather than a user, because the invitee may not have signed in yet.");
export const NewInviteSchema = z
    .object({
    email: z.string().email(),
    role: z
        .enum(["viewer", "editor", "admin", "owner"])
        .describe("Ordered least to most authority; the order is load-bearing."),
    visibility: z
        .enum(["all", "listed"])
        .describe("What a membership may see, orthogonal to its role. All by default (ADR 000046).")
        .optional(),
    projectIds: z
        .array(z
        .string()
        .regex(new RegExp("^prj_[0-9A-Za-z]{11}$"))
        .describe("A project identifier. Eleven letters and digits, no separators (ADR 000033)."))
        .describe("Which projects the membership may see when visibility is listed. Ignored when visibility is all.")
        .optional(),
})
    .strict()
    .describe("Write payload. The server owns the id, the inviter, the status and the timestamps.");
export const MemberRoleChangeSchema = z
    .object({
    role: z
        .enum(["viewer", "editor", "admin", "owner"])
        .describe("Ordered least to most authority; the order is load-bearing."),
})
    .strict()
    .describe("Write payload for changing an existing member's role.");
export const MemberVisibilityChangeSchema = z
    .object({
    visibility: z
        .enum(["all", "listed"])
        .describe("What a membership may see, orthogonal to its role. All by default (ADR 000046)."),
    projectIds: z
        .array(z
        .string()
        .regex(new RegExp("^prj_[0-9A-Za-z]{11}$"))
        .describe("A project identifier. Eleven letters and digits, no separators (ADR 000033)."))
        .describe("Which projects are visible when visibility is listed. Ignored when visibility is all.")
        .optional(),
})
    .strict()
    .describe("Write payload for narrowing or widening an existing member's project visibility.");
export const AssetSchema = z
    .object({
    id: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
    spaceId: z
        .string()
        .regex(new RegExp("^spc_[0-9A-Za-z]{11}$"))
        .describe("A space identifier. Eleven letters and digits, no separators, because it appears in an address somebody reads and types (ADR 000033)."),
    kind: z
        .enum(["video", "image", "audio", "document", "link"])
        .describe("What scenix ingests. `document` is a file of text (a PDF, a transcript, notes) and is not a Document, which is the Markdown a project edits (ADR 000028)."),
    source: z
        .enum(["upload", "youtube", "link", "generated"])
        .describe("How an asset entered the system."),
    status: z
        .enum(["pending", "analyzing", "ready", "failed"])
        .describe("Where an asset is in ingest: pending -> analyzing -> ready | failed. A pending asset past its uploadExpiresAt is abandoned and cannot reach another state (ADR 000054)."),
    name: z.string().min(1).max(500),
    storagePath: z
        .string()
        .describe("Where the original bytes live. Empty until they exist: an upload has a path reserved before anything arrives, and an import has none until the worker has fetched something (ADR 000028)."),
    manifestPath: z.string().optional(),
    sourceUrl: z
        .string()
        .describe("Where the content came from. Records what the content is rather than how the bytes arrived, so an upload may have one (ADR 000060).")
        .optional(),
    failureReason: z
        .string()
        .describe("Why the last analysis pass ended without finishing. Carried by a failed asset, and also by a ready one whose re-analysis gave up: that pass leaves the manifest as it found it rather than failing a working asset, so the reason is the only record that the request was not answered (ADR 000063, ADR 000120).")
        .optional(),
    uploadExpiresAt: z
        .string()
        .datetime({ offset: true })
        .describe("When the signed URL this asset was created with stops working. Absent for anything that never had one, which is not the same as expired long ago: an import was never waiting for bytes (ADR 000054).")
        .optional(),
    createdBy: z
        .string()
        .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    deletedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .describe("Set when the asset is in the recovery window: the record and the bytes are both still here, invisible until restored (issue #8)."),
    importMode: z
        .enum(["", "transcribe", "audio", "full"])
        .describe("How much of the source was taken, or empty for an upload, which has no source to take part of."),
    summary: z
        .string()
        .describe("Denormalised from the manifest when analysis finishes, so that listing a space does not mean pulling a manifest per asset (ADR 000028)."),
    topics: z.array(z.string()),
    durationSec: z.number().gte(0),
    sizeBytes: z.number().int().gte(0),
    thumbnailPath: z
        .string()
        .describe("A still for the browser: a frame for video, the image itself for a still, a rendered first page for a document."),
    analysisEngine: z
        .string()
        .describe("What produced the derived detail, so a systematic error is traceable to a version and a better model means a re-run (ADR 000029)."),
    analysisAttempts: z
        .number()
        .int()
        .gte(0)
        .describe("How many times a worker has claimed this. Past a small limit it fails permanently rather than retrying forever (ADR 000032)."),
})
    .strict()
    .describe("Belongs to a space, not a project, so one ingested master can back many projects.");
export const AssetThumbnailSchema = z
    .object({ atSec: z.number().gte(0), path: z.string() })
    .strict()
    .describe("A still pulled from a video at a known moment. Comes from ffprobe duration alone, so it exists for every video regardless of which vision engine is configured (issue #3).");
export const TranscriptWordSchema = z
    .object({
    text: z.string(),
    startSec: z.number().gte(0),
    endSec: z.number().gte(0),
    confidence: z.number().gte(0).lte(1).optional(),
})
    .strict()
    .describe("Word-level timing is what lets a selection snap to a sentence and a citation point at an offset.");
export const TranscriptSegmentSchema = z
    .object({
    index: z.number().int().gte(0),
    text: z.string(),
    startSec: z.number().gte(0),
    endSec: z.number().gte(0),
    speaker: z.string().optional(),
    words: z.array(z
        .object({
        text: z.string(),
        startSec: z.number().gte(0),
        endSec: z.number().gte(0),
        confidence: z.number().gte(0).lte(1).optional(),
    })
        .strict()
        .describe("Word-level timing is what lets a selection snap to a sentence and a citation point at an offset.")),
})
    .strict();
export const TranscriptSchema = z
    .object({
    language: z.string().min(2),
    engine: z.string(),
    engineVersion: z.string(),
    segments: z.array(z
        .object({
        index: z.number().int().gte(0),
        text: z.string(),
        startSec: z.number().gte(0),
        endSec: z.number().gte(0),
        speaker: z.string().optional(),
        words: z.array(z
            .object({
            text: z.string(),
            startSec: z.number().gte(0),
            endSec: z.number().gte(0),
            confidence: z.number().gte(0).lte(1).optional(),
        })
            .strict()
            .describe("Word-level timing is what lets a selection snap to a sentence and a citation point at an offset.")),
    })
        .strict()),
})
    .strict();
export const VideoStreamSchema = z
    .object({
    codec: z.string(),
    widthPx: z.number().int().gte(1),
    heightPx: z.number().int().gte(1),
    fps: z.number().gt(0),
    bitrateBps: z.number().int().optional(),
    keyframesSec: z
        .array(z.number().gte(0))
        .describe("Ascending keyframe offsets. This is what makes range extraction a stream copy."),
})
    .strict();
export const AudioStreamSchema = z
    .object({
    codec: z.string(),
    channels: z.number().int().gte(1),
    sampleRateHz: z.number().int().gte(1),
    bitrateBps: z.number().int().optional(),
})
    .strict();
export const ShotSchema = z
    .object({
    index: z.number().int().gte(0),
    startSec: z.number().gte(0),
    endSec: z.number().gte(0),
    thumbnailPath: z.string().optional(),
})
    .strict()
    .describe("A detected shot boundary: the unit a rough cut is assembled from.");
export const AssetManifestSchema = z
    .object({
    version: z.literal(1),
    assetId: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
    kind: z
        .enum(["video", "image", "audio", "document", "link"])
        .describe("What scenix ingests. `document` is a file of text (a PDF, a transcript, notes) and is not a Document, which is the Markdown a project edits (ADR 000028)."),
    durationSec: z.number().gte(0),
    container: z.string().optional(),
    sizeBytes: z.number().int().gte(0),
    checksumSha256: z.string().regex(new RegExp("^[0-9a-f]{64}$")),
    video: z.union([
        z
            .object({
            codec: z.string(),
            widthPx: z.number().int().gte(1),
            heightPx: z.number().int().gte(1),
            fps: z.number().gt(0),
            bitrateBps: z.number().int().optional(),
            keyframesSec: z
                .array(z.number().gte(0))
                .describe("Ascending keyframe offsets. This is what makes range extraction a stream copy."),
        })
            .strict(),
        z.null(),
    ]),
    audio: z.union([
        z
            .object({
            codec: z.string(),
            channels: z.number().int().gte(1),
            sampleRateHz: z.number().int().gte(1),
            bitrateBps: z.number().int().optional(),
        })
            .strict(),
        z.null(),
    ]),
    transcript: z.union([
        z
            .object({
            language: z.string().min(2),
            engine: z.string(),
            engineVersion: z.string(),
            segments: z.array(z
                .object({
                index: z.number().int().gte(0),
                text: z.string(),
                startSec: z.number().gte(0),
                endSec: z.number().gte(0),
                speaker: z.string().optional(),
                words: z.array(z
                    .object({
                    text: z.string(),
                    startSec: z.number().gte(0),
                    endSec: z.number().gte(0),
                    confidence: z.number().gte(0).lte(1).optional(),
                })
                    .strict()
                    .describe("Word-level timing is what lets a selection snap to a sentence and a citation point at an offset.")),
            })
                .strict()),
        })
            .strict(),
        z.null(),
    ]),
    text: z
        .string()
        .describe("A document's own content. What a transcript is for spoken media, for an asset with no duration (ADR 000028, ADR 000037).")
        .optional(),
    shots: z.array(z
        .object({
        index: z.number().int().gte(0),
        startSec: z.number().gte(0),
        endSec: z.number().gte(0),
        thumbnailPath: z.string().optional(),
    })
        .strict()
        .describe("A detected shot boundary: the unit a rough cut is assembled from.")),
    summary: z.string().optional(),
    topics: z.array(z.string()),
    sourceMetadata: z
        .record(z.string(), z.any())
        .describe("Whatever the source carried that scenix does not model, kept verbatim."),
    extractedAt: z.string().datetime({ offset: true }),
    audioEvents: z.array(z
        .object({
        kind: z.enum([
            "music_start",
            "music_change",
            "music_end",
            "silence",
            "speech_end",
            "laughter",
            "applause",
            "level_change",
        ]),
        startSec: z.number().gte(0),
        endSec: z.number().gte(0),
        description: z.string(),
        level: z
            .number()
            .describe("Measured value where one applies, such as dBFS for a level change.")
            .optional(),
    })
        .strict()
        .describe("A range, not an instant. This is what makes 'cut where he stops talking' answerable (ADR 000024).")),
    visuals: z.array(z
        .object({
        startSec: z.number().gte(0),
        endSec: z.number().gte(0),
        description: z
            .string()
            .describe("What is on screen, in prose: who, doing what, framed how, where."),
        onScreenText: z.string().optional(),
    })
        .strict()
        .describe("What makes 'find the harbour at dusk' a text search.")),
    energy: z.array(z
        .object({
        atSec: z.number().gte(0),
        audio: z.number().gte(0).lte(1),
        visual: z.number().gte(0).lte(1),
    })
        .strict()
        .describe("A coarse intensity curve, so 'it drags in the middle' is checkable.")),
    thumbnails: z
        .array(z
        .object({ atSec: z.number().gte(0), path: z.string() })
        .strict()
        .describe("A still pulled from a video at a known moment. Comes from ffprobe duration alone, so it exists for every video regardless of which vision engine is configured (issue #3)."))
        .describe("Stills pulled from the video at a spread of moments, so its own page can show more than the cover frame (issue #3)."),
    analysisEngine: z
        .string()
        .describe("Which engine produced the derived detail, so a systematic error is traceable to a version.")
        .optional(),
})
    .strict()
    .describe("The sidecar: everything extracted about an asset, in one document designed to be handed to a model whole.");
export const CitationSchema = z
    .object({
    kind: z.enum(["document", "asset"]),
    documentId: z
        .string()
        .regex(new RegExp("^(doc_[0-9A-HJKMNP-TV-Z]{26})?$"))
        .describe('A document id or the empty string. Go marshals a zero identifier as "" rather than omitting it, because omitempty does not apply to struct types.')
        .optional(),
    startOffset: z.number().int().gte(0).optional(),
    endOffset: z.number().int().gte(0).optional(),
    revision: z.number().int().gte(0).optional(),
    assetId: z.string().regex(new RegExp("^(ast_[0-9A-HJKMNP-TV-Z]{26})?$")).optional(),
    range: z
        .union([
        z
            .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
            .strict()
            .describe("A half-open selection over an asset's timeline, in seconds."),
        z.null(),
    ])
        .optional(),
})
    .strict()
    .describe("Where an answer came from, so a claim can be checked rather than trusted.");
export const ResolutionSchema = z
    .object({
    answer: z.string().min(1),
    resolvedBy: z.string().regex(new RegExp("^(usr_[0-9A-HJKMNP-TV-Z]{26})?$")).optional(),
    machineGenerated: z.boolean(),
    citations: z.array(z
        .object({
        kind: z.enum(["document", "asset"]),
        documentId: z
            .string()
            .regex(new RegExp("^(doc_[0-9A-HJKMNP-TV-Z]{26})?$"))
            .describe('A document id or the empty string. Go marshals a zero identifier as "" rather than omitting it, because omitempty does not apply to struct types.')
            .optional(),
        startOffset: z.number().int().gte(0).optional(),
        endOffset: z.number().int().gte(0).optional(),
        revision: z.number().int().gte(0).optional(),
        assetId: z.string().regex(new RegExp("^(ast_[0-9A-HJKMNP-TV-Z]{26})?$")).optional(),
        range: z
            .union([
            z
                .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
                .strict()
                .describe("A half-open selection over an asset's timeline, in seconds."),
            z.null(),
        ])
            .optional(),
    })
        .strict()
        .describe("Where an answer came from, so a claim can be checked rather than trusted.")),
    resolvedAt: z.string().datetime({ offset: true }),
})
    .strict()
    .describe("The accepted answer and its provenance. Only a person accepting one makes a model's proposal a decision.");
export const QuestionSchema = z
    .object({
    id: z.string().regex(new RegExp("^qst_[0-9A-HJKMNP-TV-Z]{26}$")),
    projectId: z
        .string()
        .regex(new RegExp("^prj_[0-9A-Za-z]{11}$"))
        .describe("A project identifier. Eleven letters and digits, no separators (ADR 000033)."),
    threadId: z.string().regex(new RegExp("^thr_[0-9A-HJKMNP-TV-Z]{26}$")),
    originMessageId: z.string().regex(new RegExp("^msg_[0-9A-HJKMNP-TV-Z]{26}$")),
    prompt: z.string().min(1),
    status: z.enum(["open", "answered", "needs_human", "resolved", "dismissed"]),
    assignedTo: z.string().regex(new RegExp("^(usr_[0-9A-HJKMNP-TV-Z]{26})?$")).optional(),
    resolution: z
        .object({
        answer: z.string().min(1),
        resolvedBy: z.string().regex(new RegExp("^(usr_[0-9A-HJKMNP-TV-Z]{26})?$")).optional(),
        machineGenerated: z.boolean(),
        citations: z.array(z
            .object({
            kind: z.enum(["document", "asset"]),
            documentId: z
                .string()
                .regex(new RegExp("^(doc_[0-9A-HJKMNP-TV-Z]{26})?$"))
                .describe('A document id or the empty string. Go marshals a zero identifier as "" rather than omitting it, because omitempty does not apply to struct types.')
                .optional(),
            startOffset: z.number().int().gte(0).optional(),
            endOffset: z.number().int().gte(0).optional(),
            revision: z.number().int().gte(0).optional(),
            assetId: z.string().regex(new RegExp("^(ast_[0-9A-HJKMNP-TV-Z]{26})?$")).optional(),
            range: z
                .union([
                z
                    .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
                    .strict()
                    .describe("A half-open selection over an asset's timeline, in seconds."),
                z.null(),
            ])
                .optional(),
        })
            .strict()
            .describe("Where an answer came from, so a claim can be checked rather than trusted.")),
        resolvedAt: z.string().datetime({ offset: true }),
    })
        .strict()
        .describe("The accepted answer and its provenance. Only a person accepting one makes a model's proposal a decision.")
        .optional(),
    createdBy: z
        .string()
        .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    authorKind: z
        .enum(["user", "assistant", "system"])
        .describe("Whether a person or a machine raised it. Who asked is part of what the record means, and it is decided from the credential rather than from anything the caller sends (ADR 000058)."),
})
    .strict();
export const MessageSchema = z
    .object({
    id: z.string().regex(new RegExp("^msg_[0-9A-HJKMNP-TV-Z]{26}$")),
    threadId: z.string().regex(new RegExp("^thr_[0-9A-HJKMNP-TV-Z]{26}$")),
    authorKind: z.enum(["user", "assistant", "system"]),
    authorId: z.string().regex(new RegExp("^(usr_[0-9A-HJKMNP-TV-Z]{26})?$")),
    body: z.string(),
    citations: z.array(z
        .object({
        kind: z.enum(["document", "asset"]),
        documentId: z
            .string()
            .regex(new RegExp("^(doc_[0-9A-HJKMNP-TV-Z]{26})?$"))
            .describe('A document id or the empty string. Go marshals a zero identifier as "" rather than omitting it, because omitempty does not apply to struct types.')
            .optional(),
        startOffset: z.number().int().gte(0).optional(),
        endOffset: z.number().int().gte(0).optional(),
        revision: z.number().int().gte(0).optional(),
        assetId: z.string().regex(new RegExp("^(ast_[0-9A-HJKMNP-TV-Z]{26})?$")).optional(),
        range: z
            .union([
            z
                .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
                .strict()
                .describe("A half-open selection over an asset's timeline, in seconds."),
            z.null(),
        ])
            .optional(),
    })
        .strict()
        .describe("Where an answer came from, so a claim can be checked rather than trusted.")),
    answersQuestionId: z.string().regex(new RegExp("^(qst_[0-9A-HJKMNP-TV-Z]{26})?$")),
    createdAt: z.string().datetime({ offset: true }),
})
    .strict();
export const ThreadSchema = z
    .object({
    id: z.string().regex(new RegExp("^thr_[0-9A-HJKMNP-TV-Z]{26}$")),
    projectId: z
        .string()
        .regex(new RegExp("^prj_[0-9A-Za-z]{11}$"))
        .describe("A project identifier. Eleven letters and digits, no separators (ADR 000033)."),
    createdAt: z.string().datetime({ offset: true }),
    lastMessageAt: z.string().datetime({ offset: true }).optional(),
})
    .strict()
    .describe("One long-running conversation per project, shared by everyone with access.");
export const NewMessageSchema = z
    .object({
    body: z.string().min(1),
    citations: z
        .array(z
        .object({
        kind: z.enum(["document", "asset"]),
        documentId: z
            .string()
            .regex(new RegExp("^(doc_[0-9A-HJKMNP-TV-Z]{26})?$"))
            .describe('A document id or the empty string. Go marshals a zero identifier as "" rather than omitting it, because omitempty does not apply to struct types.')
            .optional(),
        startOffset: z.number().int().gte(0).optional(),
        endOffset: z.number().int().gte(0).optional(),
        revision: z.number().int().gte(0).optional(),
        assetId: z.string().regex(new RegExp("^(ast_[0-9A-HJKMNP-TV-Z]{26})?$")).optional(),
        range: z
            .union([
            z
                .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
                .strict()
                .describe("A half-open selection over an asset's timeline, in seconds."),
            z.null(),
        ])
            .optional(),
    })
        .strict()
        .describe("Where an answer came from, so a claim can be checked rather than trusted."))
        .optional(),
    answersQuestionId: z.string().regex(new RegExp("^(qst_[0-9A-HJKMNP-TV-Z]{26})?$")).optional(),
})
    .strict()
    .describe("Write payload for posting to a project's thread. Naming answersQuestionId moves that question to answered in the same write, because the message is the proposal.");
export const NewQuestionSchema = z
    .object({ prompt: z.string().min(1) })
    .strict()
    .describe("Write payload for raising a question. Posts its own origin message in the same write.");
export const QuestionUpdateSchema = z
    .object({
    status: z.enum(["open", "answered", "needs_human", "resolved", "dismissed"]),
    answer: z.string().optional(),
    citations: z
        .array(z
        .object({
        kind: z.enum(["document", "asset"]),
        documentId: z
            .string()
            .regex(new RegExp("^(doc_[0-9A-HJKMNP-TV-Z]{26})?$"))
            .describe('A document id or the empty string. Go marshals a zero identifier as "" rather than omitting it, because omitempty does not apply to struct types.')
            .optional(),
        startOffset: z.number().int().gte(0).optional(),
        endOffset: z.number().int().gte(0).optional(),
        revision: z.number().int().gte(0).optional(),
        assetId: z.string().regex(new RegExp("^(ast_[0-9A-HJKMNP-TV-Z]{26})?$")).optional(),
        range: z
            .union([
            z
                .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
                .strict()
                .describe("A half-open selection over an asset's timeline, in seconds."),
            z.null(),
        ])
            .optional(),
    })
        .strict()
        .describe("Where an answer came from, so a claim can be checked rather than trusted."))
        .optional(),
    assignedTo: z
        .string()
        .regex(new RegExp("^(usr_[0-9A-HJKMNP-TV-Z]{26})?$"))
        .describe("Who owes the answer. Omitted leaves the assignment alone; empty clears it (ADR 000084).")
        .optional(),
})
    .strict()
    .describe("Moves a question to a new status. Answer and citations are only meaningful moving to resolved, where they become the Resolution.");
export const DocumentDraftSchema = z
    .object({
    title: z.string().min(1).max(300),
    body: z.string(),
    kind: z.enum(["treatment", "script", "outline", "note", "shot_list"]),
    baseRevision: z.number().int().gte(0).optional(),
})
    .strict()
    .describe("Write payload. The server owns the id, the revision and the timestamps.");
export const SettingsDocSchema = z
    .object({
    spaceId: z
        .string()
        .regex(new RegExp("^spc_[0-9A-Za-z]{11}$"))
        .describe("A space identifier. Eleven letters and digits, no separators, because it appears in an address somebody reads and types (ADR 000033)."),
    slug: z
        .string()
        .regex(new RegExp("^[a-z0-9]+(-[a-z0-9]+)*$"))
        .min(2)
        .max(64)
        .describe("Lowercase kebab-case, as the address of a space or project."),
    title: z.string().min(1).max(200),
    body: z.string().max(200000),
    meta: z
        .record(z.string(), z.string())
        .describe("Frontmatter, parsed on write, so a caller can read a value without understanding Markdown."),
    revision: z.number().int().gte(1),
    updatedBy: z
        .string()
        .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    deletedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .describe("Set when this settings document is in the recovery window, invisible until restored (issue #8)."),
})
    .strict()
    .describe("A space's settings: what is always true of it, sent to a model in full rather than retrieved (ADR 000037).");
export const SettingsDraftSchema = z
    .object({
    title: z.string().min(1).max(200),
    body: z.string().max(200000),
    baseRevision: z
        .number()
        .int()
        .gte(0)
        .describe("The revision this edit was made against. Zero writes unconditionally, which is what creating one means.")
        .optional(),
})
    .strict()
    .describe("Write payload for a settings document. The slug comes from the path.");
export const TimelineIdSchema = z.string().regex(new RegExp("^tml_[0-9A-HJKMNP-TV-Z]{26}$"));
export const SegmentIdSchema = z.string().regex(new RegExp("^seg_[0-9A-HJKMNP-TV-Z]{26}$"));
export const RenditionIdSchema = z.string().regex(new RegExp("^rnd_[0-9A-HJKMNP-TV-Z]{26}$"));
export const NoteIdSchema = z.string().regex(new RegExp("^nte_[0-9A-HJKMNP-TV-Z]{26}$"));
export const OptionalRenditionIdSchema = z
    .string()
    .regex(new RegExp("^(rnd_[0-9A-HJKMNP-TV-Z]{26})?$"));
export const OptionalAssetIdSchema = z
    .string()
    .regex(new RegExp("^(ast_[0-9A-HJKMNP-TV-Z]{26})?$"));
export const ProductionStageSchema = z
    .enum(["treatment", "script", "animatic", "rough_cut", "fine_cut", "master"])
    .describe("How far the work has been taken. Terms of art: an animatic is stills with timing and audio, a storyboard is static panels (ADR 000021).");
export const RenditionFidelitySchema = z
    .enum(["animatic", "rough", "fine", "master"])
    .describe("How a segment was rendered. Fidelity belongs to the segment, not the timeline: a chain routinely holds several at once (ADR 000025).");
export const RenditionStatusSchema = z.enum(["queued", "rendering", "ready", "failed"]);
export const SegmentSourceKindSchema = z
    .enum(["asset", "generated", "layout"])
    .describe("Where a segment's picture comes from. `generated` means there is no footage behind it and the direction is the whole instruction.");
export const NoteKindSchema = z
    .enum(["comment", "trim", "cut", "replace"])
    .describe("trim and cut are precise enough to apply mechanically; comment and replace need judgement.");
export const NoteStatusSchema = z
    .enum(["open", "addressed", "accepted", "dismissed"])
    .describe("addressed means the agent acted; accepted means a person agreed it worked (ADR 000023).");
export const AudioEventKindSchema = z.enum([
    "music_start",
    "music_change",
    "music_end",
    "silence",
    "speech_end",
    "laughter",
    "applause",
    "level_change",
]);
export const SegmentSchema = z
    .object({
    id: z.string().regex(new RegExp("^seg_[0-9A-HJKMNP-TV-Z]{26}$")),
    timelineId: z.string().regex(new RegExp("^tml_[0-9A-HJKMNP-TV-Z]{26}$")),
    track: z
        .number()
        .int()
        .gte(0)
        .describe("Track zero is picture, and picture is the chain of segments. Other tracks are for audio, text and overlays, and do not drive the video (ADR 000025)."),
    startSec: z.number().gte(0),
    endSec: z.number().gte(0),
    brief: z
        .object({
        kind: z
            .enum(["asset", "generated", "layout"])
            .describe("Where a segment's picture comes from. `generated` means there is no footage behind it and the direction is the whole instruction."),
        direction: z
            .string()
            .max(8000)
            .describe("What this segment is and what should happen in it. For a segment with no footage behind it, this and a length are the whole brief."),
        sources: z
            .array(z
            .object({
            assetId: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
            range: z
                .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
                .strict()
                .describe("A half-open selection over an asset's timeline, in seconds."),
            note: z
                .string()
                .max(2000)
                .describe("What is wanted from this particular source, when a segment draws on more than one and they are wanted for different reasons.")
                .optional(),
        })
            .strict()
            .describe("Footage the segment draws on, and the part of it wanted. The range is a want, not where the cut lands: extraction snaps outward to keyframes and never narrows it (ADR 000010, ADR 000026)."))
            .describe("Assets this segment draws on. Several, because a segment may cut between two clips and which parts of them is part of the instruction."),
        stills: z
            .array(z.string().min(1).max(500))
            .max(12)
            .describe("Images standing in for the picture until it exists: the frame somebody chose when they marked a moment, or one pulled from a source on the way in. Paths in the object store rather than addresses, read through the segment's stills endpoint (ADR 000070). A write that sends none keeps the ones the segment had, so a client that has never heard of them cannot drop them; one comes off by its place in the list, through the endpoint that serves it (ADR 000071). A share carries none of them."),
        narration: z
            .string()
            .max(8000)
            .describe("What is spoken over this segment, when that is settled separately from the direction."),
        layout: z.lazy(() => LayoutBoxSchema).optional(),
        renderNotes: z
            .array(z
            .object({
            key: z
                .string()
                .min(1)
                .max(64)
                .describe("What to do. Understood here: fit, background, whenShort."),
            value: z
                .string()
                .max(500)
                .describe("How. A value a renderer cannot act on is reported as unapplied too: fit takes cover or contain, whenShort takes black or hold, and a background has to be a colour the renderer knows.")
                .optional(),
            why: z
                .string()
                .max(2000)
                .describe("For the person who reads this in six months and would otherwise delete it.")
                .optional(),
        })
            .strict()
            .describe("One instruction addressed to whatever renders this, where direction is prose for a person and must not be interpreted by a machine. A key this renderer does not understand is carried and reported as unapplied rather than refused (ADR 000068)."))
            .describe("Instructions for whatever renders this (ADR 000068).")
            .optional(),
    })
        .strict()
        .describe("The complete instruction for making one segment, and the only input to rendering it (ADR 000026). Length is deliberately not here: it is the segment's span on the timeline, edited by dragging its edges."),
    state: z
        .enum(["planned", "queued", "rendering", "ready", "failed", "stale"])
        .describe("Whether a segment's picture has been made. `planned` is the storyboard: there is no storyboard entity, only segments in this state (ADR 000025)."),
    renditionId: z
        .string()
        .regex(new RegExp("^(rnd_[0-9A-HJKMNP-TV-Z]{26})?$"))
        .describe("The rendition currently playing for this segment. Empty while planned."),
    derivedFromId: z
        .string()
        .regex(new RegExp("^(seg_[0-9A-HJKMNP-TV-Z]{26})?$"))
        .describe("The segment this one was split off from, so a note left before the split can still find the pieces (ADR 000025)."),
    label: z.string(),
})
    .strict()
    .describe("One link in the chain that is the video (ADR 000025). Time is the coordinate system: nothing here refers into a rendered file.");
export const RenditionSchema = z
    .object({
    id: z.string().regex(new RegExp("^rnd_[0-9A-HJKMNP-TV-Z]{26}$")),
    timelineId: z.string().regex(new RegExp("^tml_[0-9A-HJKMNP-TV-Z]{26}$")),
    segmentId: z
        .string()
        .regex(new RegExp("^(seg_[0-9A-HJKMNP-TV-Z]{26})?$"))
        .describe("The segment this renders. Empty for the assembly of the whole chain."),
    timelineVersion: z.number().int().gte(1),
    fidelity: z
        .enum(["animatic", "rough", "fine", "master"])
        .describe("How a segment was rendered. Fidelity belongs to the segment, not the timeline: a chain routinely holds several at once (ADR 000025)."),
    status: z.enum(["queued", "rendering", "ready", "failed"]),
    briefFingerprint: z
        .string()
        .describe("The brief this was made from, hashed. Comparing it against the segment's current brief is what makes a segment stale, and it is the cache key that stops an unchanged segment being rendered twice (ADR 000026)."),
    storagePath: z.string(),
    posterPath: z.string(),
    durationSec: z.number().gte(0),
    failureReason: z.string(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    unappliedNotes: z
        .array(z.string())
        .describe("Render notes whatever made this could not follow. An instruction that quietly does nothing is worse than one that is refused (ADR 000068).")
        .optional(),
})
    .strict()
    .describe("A made artifact. Belongs to the segment it renders; a rendition with no segment is the whole chain assembled as one deliverable (ADR 000025).");
export const TimelineSchema = z
    .object({
    id: z.string().regex(new RegExp("^tml_[0-9A-HJKMNP-TV-Z]{26}$")),
    projectId: z
        .string()
        .regex(new RegExp("^prj_[0-9A-Za-z]{11}$"))
        .describe("A project identifier. Eleven letters and digits, no separators (ADR 000033)."),
    version: z
        .number()
        .int()
        .gte(1)
        .describe("Every accepted revision produces a new version. Superseded versions are immutable, so a note keeps meaning what it meant."),
    durationSec: z
        .number()
        .gte(0)
        .describe("Where the chain ends: the planned length, which is what the briefs say."),
    trackCount: z.number().int().gte(1),
    createdBy: z
        .string()
        .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    renderedDurationSec: z
        .number()
        .gte(0)
        .describe("The length of the segments that have actually been made. Drifts from the planned length as sections come out longer or shorter than budgeted, and both are kept rather than one quietly winning (ADR 000025)."),
    readyDurationSec: z
        .number()
        .gte(0)
        .describe("How much of the planned length is made. Over durationSec this is progress: '26 of 40 minutes', which a project stage cannot express."),
})
    .strict()
    .describe("The chain that is the video, one per project. It does not change when the stage does; its segments gain pictures (ADR 000025).");
export const TimelineWithSegmentsSchema = z
    .object({
    timeline: z
        .object({
        id: z.string().regex(new RegExp("^tml_[0-9A-HJKMNP-TV-Z]{26}$")),
        projectId: z
            .string()
            .regex(new RegExp("^prj_[0-9A-Za-z]{11}$"))
            .describe("A project identifier. Eleven letters and digits, no separators (ADR 000033)."),
        version: z
            .number()
            .int()
            .gte(1)
            .describe("Every accepted revision produces a new version. Superseded versions are immutable, so a note keeps meaning what it meant."),
        durationSec: z
            .number()
            .gte(0)
            .describe("Where the chain ends: the planned length, which is what the briefs say."),
        trackCount: z.number().int().gte(1),
        createdBy: z
            .string()
            .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
            .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
        createdAt: z.string().datetime({ offset: true }),
        updatedAt: z.string().datetime({ offset: true }),
        renderedDurationSec: z
            .number()
            .gte(0)
            .describe("The length of the segments that have actually been made. Drifts from the planned length as sections come out longer or shorter than budgeted, and both are kept rather than one quietly winning (ADR 000025)."),
        readyDurationSec: z
            .number()
            .gte(0)
            .describe("How much of the planned length is made. Over durationSec this is progress: '26 of 40 minutes', which a project stage cannot express."),
    })
        .strict()
        .describe("The chain that is the video, one per project. It does not change when the stage does; its segments gain pictures (ADR 000025)."),
    segments: z.array(z
        .object({
        id: z.string().regex(new RegExp("^seg_[0-9A-HJKMNP-TV-Z]{26}$")),
        timelineId: z.string().regex(new RegExp("^tml_[0-9A-HJKMNP-TV-Z]{26}$")),
        track: z
            .number()
            .int()
            .gte(0)
            .describe("Track zero is picture, and picture is the chain of segments. Other tracks are for audio, text and overlays, and do not drive the video (ADR 000025)."),
        startSec: z.number().gte(0),
        endSec: z.number().gte(0),
        brief: z
            .object({
            kind: z
                .enum(["asset", "generated", "layout"])
                .describe("Where a segment's picture comes from. `generated` means there is no footage behind it and the direction is the whole instruction."),
            direction: z
                .string()
                .max(8000)
                .describe("What this segment is and what should happen in it. For a segment with no footage behind it, this and a length are the whole brief."),
            sources: z
                .array(z
                .object({
                assetId: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
                range: z
                    .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
                    .strict()
                    .describe("A half-open selection over an asset's timeline, in seconds."),
                note: z
                    .string()
                    .max(2000)
                    .describe("What is wanted from this particular source, when a segment draws on more than one and they are wanted for different reasons.")
                    .optional(),
            })
                .strict()
                .describe("Footage the segment draws on, and the part of it wanted. The range is a want, not where the cut lands: extraction snaps outward to keyframes and never narrows it (ADR 000010, ADR 000026)."))
                .describe("Assets this segment draws on. Several, because a segment may cut between two clips and which parts of them is part of the instruction."),
            stills: z
                .array(z.string().min(1).max(500))
                .max(12)
                .describe("Images standing in for the picture until it exists: the frame somebody chose when they marked a moment, or one pulled from a source on the way in. Paths in the object store rather than addresses, read through the segment's stills endpoint (ADR 000070). A write that sends none keeps the ones the segment had, so a client that has never heard of them cannot drop them; one comes off by its place in the list, through the endpoint that serves it (ADR 000071). A share carries none of them."),
            narration: z
                .string()
                .max(8000)
                .describe("What is spoken over this segment, when that is settled separately from the direction."),
            layout: z.lazy(() => LayoutBoxSchema).optional(),
            renderNotes: z
                .array(z
                .object({
                key: z
                    .string()
                    .min(1)
                    .max(64)
                    .describe("What to do. Understood here: fit, background, whenShort."),
                value: z
                    .string()
                    .max(500)
                    .describe("How. A value a renderer cannot act on is reported as unapplied too: fit takes cover or contain, whenShort takes black or hold, and a background has to be a colour the renderer knows.")
                    .optional(),
                why: z
                    .string()
                    .max(2000)
                    .describe("For the person who reads this in six months and would otherwise delete it.")
                    .optional(),
            })
                .strict()
                .describe("One instruction addressed to whatever renders this, where direction is prose for a person and must not be interpreted by a machine. A key this renderer does not understand is carried and reported as unapplied rather than refused (ADR 000068)."))
                .describe("Instructions for whatever renders this (ADR 000068).")
                .optional(),
        })
            .strict()
            .describe("The complete instruction for making one segment, and the only input to rendering it (ADR 000026). Length is deliberately not here: it is the segment's span on the timeline, edited by dragging its edges."),
        state: z
            .enum(["planned", "queued", "rendering", "ready", "failed", "stale"])
            .describe("Whether a segment's picture has been made. `planned` is the storyboard: there is no storyboard entity, only segments in this state (ADR 000025)."),
        renditionId: z
            .string()
            .regex(new RegExp("^(rnd_[0-9A-HJKMNP-TV-Z]{26})?$"))
            .describe("The rendition currently playing for this segment. Empty while planned."),
        derivedFromId: z
            .string()
            .regex(new RegExp("^(seg_[0-9A-HJKMNP-TV-Z]{26})?$"))
            .describe("The segment this one was split off from, so a note left before the split can still find the pieces (ADR 000025)."),
        label: z.string(),
    })
        .strict()
        .describe("One link in the chain that is the video (ADR 000025). Time is the coordinate system: nothing here refers into a rendered file.")),
    renditions: z.array(z
        .object({
        id: z.string().regex(new RegExp("^rnd_[0-9A-HJKMNP-TV-Z]{26}$")),
        timelineId: z.string().regex(new RegExp("^tml_[0-9A-HJKMNP-TV-Z]{26}$")),
        segmentId: z
            .string()
            .regex(new RegExp("^(seg_[0-9A-HJKMNP-TV-Z]{26})?$"))
            .describe("The segment this renders. Empty for the assembly of the whole chain."),
        timelineVersion: z.number().int().gte(1),
        fidelity: z
            .enum(["animatic", "rough", "fine", "master"])
            .describe("How a segment was rendered. Fidelity belongs to the segment, not the timeline: a chain routinely holds several at once (ADR 000025)."),
        status: z.enum(["queued", "rendering", "ready", "failed"]),
        briefFingerprint: z
            .string()
            .describe("The brief this was made from, hashed. Comparing it against the segment's current brief is what makes a segment stale, and it is the cache key that stops an unchanged segment being rendered twice (ADR 000026)."),
        storagePath: z.string(),
        posterPath: z.string(),
        durationSec: z.number().gte(0),
        failureReason: z.string(),
        createdAt: z.string().datetime({ offset: true }),
        updatedAt: z.string().datetime({ offset: true }),
        unappliedNotes: z
            .array(z.string())
            .describe("Render notes whatever made this could not follow. An instruction that quietly does nothing is worse than one that is refused (ADR 000068).")
            .optional(),
    })
        .strict()
        .describe("A made artifact. Belongs to the segment it renders; a rendition with no segment is the whole chain assembled as one deliverable (ADR 000025).")),
    mediaUrls: z
        .record(z.string(), z.string())
        .describe("Short-lived signed URLs keyed by rendition id, for the renditions that are ready. Nobody watching a cut learns a storage path."),
    posterUrls: z
        .record(z.string(), z.string())
        .describe("The same, for the still each ready rendition carries."),
    assemblyFingerprint: z
        .string()
        .describe("What an assembly of this chain would be made from as it stands, and empty when there is nothing to assemble. An assembly rendition is the current deliverable only while its briefFingerprint matches this one (ADR 000061)."),
})
    .strict()
    .describe("What the scrub bar needs in one response.");
export const NoteSchema = z
    .object({
    id: z.string().regex(new RegExp("^nte_[0-9A-HJKMNP-TV-Z]{26}$")),
    projectId: z
        .string()
        .regex(new RegExp("^prj_[0-9A-Za-z]{11}$"))
        .describe("A project identifier. Eleven letters and digits, no separators (ADR 000033)."),
    timelineId: z.string().regex(new RegExp("^tml_[0-9A-HJKMNP-TV-Z]{26}$")),
    timelineVersion: z
        .number()
        .int()
        .gte(1)
        .describe("The version the range refers to. A note taken against version 3 keeps meaning version 3's timing."),
    track: z.number().int().gte(0),
    range: z
        .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
        .strict()
        .describe("A half-open selection over an asset's timeline, in seconds."),
    kind: z
        .enum(["comment", "trim", "cut", "replace"])
        .describe("trim and cut are precise enough to apply mechanically; comment and replace need judgement."),
    body: z.string(),
    status: z
        .enum(["open", "addressed", "accepted", "dismissed"])
        .describe("addressed means the agent acted; accepted means a person agreed it worked (ADR 000023)."),
    authorKind: z.enum(["user", "assistant", "system"]),
    authorId: z.string().regex(new RegExp("^(usr_[0-9A-HJKMNP-TV-Z]{26})?$")),
    questionId: z.string().regex(new RegExp("^(qst_[0-9A-HJKMNP-TV-Z]{26})?$")),
    resolutionNote: z.string().describe("What the agent did about it."),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
})
    .strict()
    .describe("A range on a timeline version and what is wrong with it (ADR 000023).");
export const NewNoteSchema = z
    .object({
    range: z
        .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
        .strict()
        .describe("A half-open selection over an asset's timeline, in seconds."),
    kind: z
        .enum(["comment", "trim", "cut", "replace"])
        .describe("trim and cut are precise enough to apply mechanically; comment and replace need judgement."),
    body: z.string().max(4000).optional(),
    track: z.number().int().gte(0).optional(),
})
    .strict()
    .describe("Write payload. The server owns the id, the author, the version and the timestamps.");
export const AudioEventSchema = z
    .object({
    kind: z.enum([
        "music_start",
        "music_change",
        "music_end",
        "silence",
        "speech_end",
        "laughter",
        "applause",
        "level_change",
    ]),
    startSec: z.number().gte(0),
    endSec: z.number().gte(0),
    description: z.string(),
    level: z
        .number()
        .describe("Measured value where one applies, such as dBFS for a level change.")
        .optional(),
})
    .strict()
    .describe("A range, not an instant. This is what makes 'cut where he stops talking' answerable (ADR 000024).");
export const VisualDescriptionSchema = z
    .object({
    startSec: z.number().gte(0),
    endSec: z.number().gte(0),
    description: z
        .string()
        .describe("What is on screen, in prose: who, doing what, framed how, where."),
    onScreenText: z.string().optional(),
})
    .strict()
    .describe("What makes 'find the harbour at dusk' a text search.");
export const EnergyPointSchema = z
    .object({
    atSec: z.number().gte(0),
    audio: z.number().gte(0).lte(1),
    visual: z.number().gte(0).lte(1),
})
    .strict()
    .describe("A coarse intensity curve, so 'it drags in the middle' is checkable.");
export const SegmentStateSchema = z
    .enum(["planned", "queued", "rendering", "ready", "failed", "stale"])
    .describe("Whether a segment's picture has been made. `planned` is the storyboard: there is no storyboard entity, only segments in this state (ADR 000025).");
export const BriefSourceSchema = z
    .object({
    assetId: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
    range: z
        .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
        .strict()
        .describe("A half-open selection over an asset's timeline, in seconds."),
    note: z
        .string()
        .max(2000)
        .describe("What is wanted from this particular source, when a segment draws on more than one and they are wanted for different reasons.")
        .optional(),
})
    .strict()
    .describe("Footage the segment draws on, and the part of it wanted. The range is a want, not where the cut lands: extraction snaps outward to keyframes and never narrows it (ADR 000010, ADR 000026).");
export const SegmentBriefSchema = z
    .object({
    kind: z
        .enum(["asset", "generated", "layout"])
        .describe("Where a segment's picture comes from. `generated` means there is no footage behind it and the direction is the whole instruction."),
    direction: z
        .string()
        .max(8000)
        .describe("What this segment is and what should happen in it. For a segment with no footage behind it, this and a length are the whole brief."),
    sources: z
        .array(z
        .object({
        assetId: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
        range: z
            .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
            .strict()
            .describe("A half-open selection over an asset's timeline, in seconds."),
        note: z
            .string()
            .max(2000)
            .describe("What is wanted from this particular source, when a segment draws on more than one and they are wanted for different reasons.")
            .optional(),
    })
        .strict()
        .describe("Footage the segment draws on, and the part of it wanted. The range is a want, not where the cut lands: extraction snaps outward to keyframes and never narrows it (ADR 000010, ADR 000026)."))
        .describe("Assets this segment draws on. Several, because a segment may cut between two clips and which parts of them is part of the instruction."),
    stills: z
        .array(z.string().min(1).max(500))
        .max(12)
        .describe("Images standing in for the picture until it exists: the frame somebody chose when they marked a moment, or one pulled from a source on the way in. Paths in the object store rather than addresses, read through the segment's stills endpoint (ADR 000070). A write that sends none keeps the ones the segment had, so a client that has never heard of them cannot drop them; one comes off by its place in the list, through the endpoint that serves it (ADR 000071). A share carries none of them."),
    narration: z
        .string()
        .max(8000)
        .describe("What is spoken over this segment, when that is settled separately from the direction."),
    layout: z.lazy(() => LayoutBoxSchema).optional(),
    renderNotes: z
        .array(z
        .object({
        key: z
            .string()
            .min(1)
            .max(64)
            .describe("What to do. Understood here: fit, background, whenShort."),
        value: z
            .string()
            .max(500)
            .describe("How. A value a renderer cannot act on is reported as unapplied too: fit takes cover or contain, whenShort takes black or hold, and a background has to be a colour the renderer knows.")
            .optional(),
        why: z
            .string()
            .max(2000)
            .describe("For the person who reads this in six months and would otherwise delete it.")
            .optional(),
    })
        .strict()
        .describe("One instruction addressed to whatever renders this, where direction is prose for a person and must not be interpreted by a machine. A key this renderer does not understand is carried and reported as unapplied rather than refused (ADR 000068)."))
        .describe("Instructions for whatever renders this (ADR 000068).")
        .optional(),
})
    .strict()
    .describe("The complete instruction for making one segment, and the only input to rendering it (ADR 000026). Length is deliberately not here: it is the segment's span on the timeline, edited by dragging its edges.");
export const OptionalSegmentIdSchema = z
    .string()
    .regex(new RegExp("^(seg_[0-9A-HJKMNP-TV-Z]{26})?$"))
    .describe("A seg identifier, or empty when there is none.");
export const SegmentDraftSchema = z
    .object({
    id: z
        .string()
        .regex(new RegExp("^(seg_[0-9A-HJKMNP-TV-Z]{26})?$"))
        .describe("Present when this is an existing segment being kept or moved. Absent when it is new.")
        .optional(),
    track: z.number().int().gte(0),
    startSec: z.number().gte(0),
    endSec: z.number().gte(0),
    brief: z
        .object({
        kind: z
            .enum(["asset", "generated", "layout"])
            .describe("Where a segment's picture comes from. `generated` means there is no footage behind it and the direction is the whole instruction."),
        direction: z
            .string()
            .max(8000)
            .describe("What this segment is and what should happen in it. For a segment with no footage behind it, this and a length are the whole brief."),
        sources: z
            .array(z
            .object({
            assetId: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
            range: z
                .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
                .strict()
                .describe("A half-open selection over an asset's timeline, in seconds."),
            note: z
                .string()
                .max(2000)
                .describe("What is wanted from this particular source, when a segment draws on more than one and they are wanted for different reasons.")
                .optional(),
        })
            .strict()
            .describe("Footage the segment draws on, and the part of it wanted. The range is a want, not where the cut lands: extraction snaps outward to keyframes and never narrows it (ADR 000010, ADR 000026)."))
            .describe("Assets this segment draws on. Several, because a segment may cut between two clips and which parts of them is part of the instruction."),
        stills: z
            .array(z.string().min(1).max(500))
            .max(12)
            .describe("Images standing in for the picture until it exists: the frame somebody chose when they marked a moment, or one pulled from a source on the way in. Paths in the object store rather than addresses, read through the segment's stills endpoint (ADR 000070). A write that sends none keeps the ones the segment had, so a client that has never heard of them cannot drop them; one comes off by its place in the list, through the endpoint that serves it (ADR 000071). A share carries none of them."),
        narration: z
            .string()
            .max(8000)
            .describe("What is spoken over this segment, when that is settled separately from the direction."),
        layout: z.lazy(() => LayoutBoxSchema).optional(),
        renderNotes: z
            .array(z
            .object({
            key: z
                .string()
                .min(1)
                .max(64)
                .describe("What to do. Understood here: fit, background, whenShort."),
            value: z
                .string()
                .max(500)
                .describe("How. A value a renderer cannot act on is reported as unapplied too: fit takes cover or contain, whenShort takes black or hold, and a background has to be a colour the renderer knows.")
                .optional(),
            why: z
                .string()
                .max(2000)
                .describe("For the person who reads this in six months and would otherwise delete it.")
                .optional(),
        })
            .strict()
            .describe("One instruction addressed to whatever renders this, where direction is prose for a person and must not be interpreted by a machine. A key this renderer does not understand is carried and reported as unapplied rather than refused (ADR 000068)."))
            .describe("Instructions for whatever renders this (ADR 000068).")
            .optional(),
    })
        .strict()
        .describe("The complete instruction for making one segment, and the only input to rendering it (ADR 000026). Length is deliberately not here: it is the segment's span on the timeline, edited by dragging its edges."),
    label: z.string().max(300).optional(),
    derivedFromId: z
        .string()
        .regex(new RegExp("^(seg_[0-9A-HJKMNP-TV-Z]{26})?$"))
        .describe("A seg identifier, or empty when there is none.")
        .optional(),
})
    .strict()
    .describe("A segment on its way to the server. The state, the current rendition and the timeline it belongs to are the server's to decide; a segment being created for the first time has no id and the server mints one.");
export const ReplaceSegmentsSchema = z
    .object({
    expectedVersion: z
        .number()
        .int()
        .gte(0)
        .describe("The timeline version this edit was made against. A write naming a version the timeline has passed is refused with 409. Zero means whatever is there now, which is for the first write to an empty chain (ADR 000027).")
        .optional(),
    segments: z.array(z
        .object({
        id: z
            .string()
            .regex(new RegExp("^(seg_[0-9A-HJKMNP-TV-Z]{26})?$"))
            .describe("Present when this is an existing segment being kept or moved. Absent when it is new.")
            .optional(),
        track: z.number().int().gte(0),
        startSec: z.number().gte(0),
        endSec: z.number().gte(0),
        brief: z
            .object({
            kind: z
                .enum(["asset", "generated", "layout"])
                .describe("Where a segment's picture comes from. `generated` means there is no footage behind it and the direction is the whole instruction."),
            direction: z
                .string()
                .max(8000)
                .describe("What this segment is and what should happen in it. For a segment with no footage behind it, this and a length are the whole brief."),
            sources: z
                .array(z
                .object({
                assetId: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
                range: z
                    .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
                    .strict()
                    .describe("A half-open selection over an asset's timeline, in seconds."),
                note: z
                    .string()
                    .max(2000)
                    .describe("What is wanted from this particular source, when a segment draws on more than one and they are wanted for different reasons.")
                    .optional(),
            })
                .strict()
                .describe("Footage the segment draws on, and the part of it wanted. The range is a want, not where the cut lands: extraction snaps outward to keyframes and never narrows it (ADR 000010, ADR 000026)."))
                .describe("Assets this segment draws on. Several, because a segment may cut between two clips and which parts of them is part of the instruction."),
            stills: z
                .array(z.string().min(1).max(500))
                .max(12)
                .describe("Images standing in for the picture until it exists: the frame somebody chose when they marked a moment, or one pulled from a source on the way in. Paths in the object store rather than addresses, read through the segment's stills endpoint (ADR 000070). A write that sends none keeps the ones the segment had, so a client that has never heard of them cannot drop them; one comes off by its place in the list, through the endpoint that serves it (ADR 000071). A share carries none of them."),
            narration: z
                .string()
                .max(8000)
                .describe("What is spoken over this segment, when that is settled separately from the direction."),
            layout: z.lazy(() => LayoutBoxSchema).optional(),
            renderNotes: z
                .array(z
                .object({
                key: z
                    .string()
                    .min(1)
                    .max(64)
                    .describe("What to do. Understood here: fit, background, whenShort."),
                value: z
                    .string()
                    .max(500)
                    .describe("How. A value a renderer cannot act on is reported as unapplied too: fit takes cover or contain, whenShort takes black or hold, and a background has to be a colour the renderer knows.")
                    .optional(),
                why: z
                    .string()
                    .max(2000)
                    .describe("For the person who reads this in six months and would otherwise delete it.")
                    .optional(),
            })
                .strict()
                .describe("One instruction addressed to whatever renders this, where direction is prose for a person and must not be interpreted by a machine. A key this renderer does not understand is carried and reported as unapplied rather than refused (ADR 000068)."))
                .describe("Instructions for whatever renders this (ADR 000068).")
                .optional(),
        })
            .strict()
            .describe("The complete instruction for making one segment, and the only input to rendering it (ADR 000026). Length is deliberately not here: it is the segment's span on the timeline, edited by dragging its edges."),
        label: z.string().max(300).optional(),
        derivedFromId: z
            .string()
            .regex(new RegExp("^(seg_[0-9A-HJKMNP-TV-Z]{26})?$"))
            .describe("A seg identifier, or empty when there is none.")
            .optional(),
    })
        .strict()
        .describe("A segment on its way to the server. The state, the current rendition and the timeline it belongs to are the server's to decide; a segment being created for the first time has no id and the server mints one.")),
})
    .strict()
    .describe("Write the whole chain. The set is replaced, not merged: an edit shifts the timings of everything after it, and non-overlap is only checkable against the complete set (ADR 000025).");
export const SegmentChangeSchema = z
    .object({
    expectedVersion: z
        .number()
        .int()
        .gte(0)
        .describe("The timeline version this edit was made against. A write naming a version the timeline has passed is refused with 409. Zero means whatever is there now, which is for the first write to an empty chain (ADR 000027).")
        .optional(),
    startSec: z.number().gte(0).optional(),
    endSec: z.number().gte(0).optional(),
    track: z.number().int().gte(0).optional(),
    brief: z
        .object({
        kind: z
            .enum(["asset", "generated", "layout"])
            .describe("Where a segment's picture comes from. `generated` means there is no footage behind it and the direction is the whole instruction."),
        direction: z
            .string()
            .max(8000)
            .describe("What this segment is and what should happen in it. For a segment with no footage behind it, this and a length are the whole brief."),
        sources: z
            .array(z
            .object({
            assetId: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
            range: z
                .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
                .strict()
                .describe("A half-open selection over an asset's timeline, in seconds."),
            note: z
                .string()
                .max(2000)
                .describe("What is wanted from this particular source, when a segment draws on more than one and they are wanted for different reasons.")
                .optional(),
        })
            .strict()
            .describe("Footage the segment draws on, and the part of it wanted. The range is a want, not where the cut lands: extraction snaps outward to keyframes and never narrows it (ADR 000010, ADR 000026)."))
            .describe("Assets this segment draws on. Several, because a segment may cut between two clips and which parts of them is part of the instruction."),
        stills: z
            .array(z.string().min(1).max(500))
            .max(12)
            .describe("Images standing in for the picture until it exists: the frame somebody chose when they marked a moment, or one pulled from a source on the way in. Paths in the object store rather than addresses, read through the segment's stills endpoint (ADR 000070). A write that sends none keeps the ones the segment had, so a client that has never heard of them cannot drop them; one comes off by its place in the list, through the endpoint that serves it (ADR 000071). A share carries none of them."),
        narration: z
            .string()
            .max(8000)
            .describe("What is spoken over this segment, when that is settled separately from the direction."),
        layout: z.lazy(() => LayoutBoxSchema).optional(),
        renderNotes: z
            .array(z
            .object({
            key: z
                .string()
                .min(1)
                .max(64)
                .describe("What to do. Understood here: fit, background, whenShort."),
            value: z
                .string()
                .max(500)
                .describe("How. A value a renderer cannot act on is reported as unapplied too: fit takes cover or contain, whenShort takes black or hold, and a background has to be a colour the renderer knows.")
                .optional(),
            why: z
                .string()
                .max(2000)
                .describe("For the person who reads this in six months and would otherwise delete it.")
                .optional(),
        })
            .strict()
            .describe("One instruction addressed to whatever renders this, where direction is prose for a person and must not be interpreted by a machine. A key this renderer does not understand is carried and reported as unapplied rather than refused (ADR 000068)."))
            .describe("Instructions for whatever renders this (ADR 000068).")
            .optional(),
    })
        .strict()
        .describe("The complete instruction for making one segment, and the only input to rendering it (ADR 000026). Length is deliberately not here: it is the segment's span on the timeline, edited by dragging its edges.")
        .optional(),
    label: z.string().max(300).optional(),
    renderNotes: z
        .array(z
        .object({
        key: z
            .string()
            .min(1)
            .max(64)
            .describe("What to do. Understood here: fit, background, whenShort."),
        value: z
            .string()
            .max(500)
            .describe("How. A value a renderer cannot act on is reported as unapplied too: fit takes cover or contain, whenShort takes black or hold, and a background has to be a colour the renderer knows.")
            .optional(),
        why: z
            .string()
            .max(2000)
            .describe("For the person who reads this in six months and would otherwise delete it.")
            .optional(),
    })
        .strict()
        .describe("One instruction addressed to whatever renders this, where direction is prose for a person and must not be interpreted by a machine. A key this renderer does not understand is carried and reported as unapplied rather than refused (ADR 000068)."))
        .describe("Replaces the segment's instructions to the renderer (ADR 000068). An empty list clears them, where an absent field leaves them alone.")
        .optional(),
})
    .strict()
    .describe("An edit to one segment, every field optional. The operation an agent uses for almost everything, because almost every instruction is about one segment (ADR 000027). An absent field is left alone.");
export const ImportModeSchema = z
    .enum(["transcribe", "audio", "full"])
    .describe("How much of a source to take. Recorded rather than inferred, because what was taken from somebody else's video is a question worth being able to answer later (ADR 000028).");
export const NewAssetSchema = z
    .object({
    kind: z
        .enum(["video", "image", "audio", "document", "link"])
        .describe("What scenix ingests. `document` is a file of text (a PDF, a transcript, notes) and is not a Document, which is the Markdown a project edits (ADR 000028)."),
    source: z
        .enum(["upload", "youtube", "link", "generated"])
        .describe("How an asset entered the system."),
    name: z.string().min(1).max(300),
    filename: z
        .string()
        .max(300)
        .describe("What the bytes will be stored as. The extension matters: it is what a content type is derived from and what ffprobe uses as a hint.")
        .optional(),
    contentType: z.string().max(200).optional(),
    sourceUrl: z
        .string()
        .max(2000)
        .describe("Where to fetch from, for a youtube or link source, and where an upload's content came from. A youtube source is held to an address YouTube serves from, because it is what the ingest worker hands to yt-dlp (ADR 000122).")
        .optional(),
    importMode: z
        .enum(["transcribe", "audio", "full"])
        .describe("How much of a source to take. Recorded rather than inferred, because what was taken from somebody else's video is a question worth being able to answer later (ADR 000028).")
        .optional(),
    text: z
        .string()
        .max(200000)
        .describe("A document's content, inline. Only for kind document with source generated: it skips the signed upload and goes straight to analysis, so writing something down is one call (ADR 000037).")
        .optional(),
})
    .strict()
    .describe("What a caller sends to bring content in. The id, the storage path, the status and everything analysis produces are the server's (ADR 000028).");
export const AssetChangeSchema = z
    .object({
    name: z.string().min(1).max(300).optional(),
    sourceUrl: z
        .string()
        .max(2000)
        .describe("Where the content came from, as an http or https address. Empty clears one attached to the wrong asset. An upload may carry one: this records what the content is, not how the bytes reached us. On a youtube asset it is instead the address the video is fetched from, so it is held to one YouTube serves from and cannot be cleared (ADR 000123).")
        .optional(),
})
    .strict()
    .describe("Corrects what a person wrote about an asset. Every field is optional and absent means unchanged. Only what a person authored is here: what the system measured has no field to arrive in (ADR 000060).");
export const AssetUploadSchema = z
    .object({
    asset: z
        .object({
        id: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
        spaceId: z
            .string()
            .regex(new RegExp("^spc_[0-9A-Za-z]{11}$"))
            .describe("A space identifier. Eleven letters and digits, no separators, because it appears in an address somebody reads and types (ADR 000033)."),
        kind: z
            .enum(["video", "image", "audio", "document", "link"])
            .describe("What scenix ingests. `document` is a file of text (a PDF, a transcript, notes) and is not a Document, which is the Markdown a project edits (ADR 000028)."),
        source: z
            .enum(["upload", "youtube", "link", "generated"])
            .describe("How an asset entered the system."),
        status: z
            .enum(["pending", "analyzing", "ready", "failed"])
            .describe("Where an asset is in ingest: pending -> analyzing -> ready | failed. A pending asset past its uploadExpiresAt is abandoned and cannot reach another state (ADR 000054)."),
        name: z.string().min(1).max(500),
        storagePath: z
            .string()
            .describe("Where the original bytes live. Empty until they exist: an upload has a path reserved before anything arrives, and an import has none until the worker has fetched something (ADR 000028)."),
        manifestPath: z.string().optional(),
        sourceUrl: z
            .string()
            .describe("Where the content came from. Records what the content is rather than how the bytes arrived, so an upload may have one (ADR 000060).")
            .optional(),
        failureReason: z
            .string()
            .describe("Why the last analysis pass ended without finishing. Carried by a failed asset, and also by a ready one whose re-analysis gave up: that pass leaves the manifest as it found it rather than failing a working asset, so the reason is the only record that the request was not answered (ADR 000063, ADR 000120).")
            .optional(),
        uploadExpiresAt: z
            .string()
            .datetime({ offset: true })
            .describe("When the signed URL this asset was created with stops working. Absent for anything that never had one, which is not the same as expired long ago: an import was never waiting for bytes (ADR 000054).")
            .optional(),
        createdBy: z
            .string()
            .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
            .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
        createdAt: z.string().datetime({ offset: true }),
        updatedAt: z.string().datetime({ offset: true }),
        deletedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .describe("Set when the asset is in the recovery window: the record and the bytes are both still here, invisible until restored (issue #8)."),
        importMode: z
            .enum(["", "transcribe", "audio", "full"])
            .describe("How much of the source was taken, or empty for an upload, which has no source to take part of."),
        summary: z
            .string()
            .describe("Denormalised from the manifest when analysis finishes, so that listing a space does not mean pulling a manifest per asset (ADR 000028)."),
        topics: z.array(z.string()),
        durationSec: z.number().gte(0),
        sizeBytes: z.number().int().gte(0),
        thumbnailPath: z
            .string()
            .describe("A still for the browser: a frame for video, the image itself for a still, a rendered first page for a document."),
        analysisEngine: z
            .string()
            .describe("What produced the derived detail, so a systematic error is traceable to a version and a better model means a re-run (ADR 000029)."),
        analysisAttempts: z
            .number()
            .int()
            .gte(0)
            .describe("How many times a worker has claimed this. Past a small limit it fails permanently rather than retrying forever (ADR 000032)."),
    })
        .strict()
        .describe("Belongs to a space, not a project, so one ingested master can back many projects."),
    uploadUrl: z
        .string()
        .describe("Empty when there is nothing to upload, which is every import and every link."),
    expiresAt: z.string().datetime({ offset: true }),
})
    .strict()
    .describe("A newly created asset and, for an upload, the signed URL to PUT the bytes to. Media never passes through the API (ADR 000028).");
export const OptionalImportModeSchema = z
    .enum(["", "transcribe", "audio", "full"])
    .describe("How much of the source was taken, or empty for an upload, which has no source to take part of.");
export const AgentKeyIdSchema = z.string().regex(new RegExp("^agk_[0-9A-HJKMNP-TV-Z]{26}$"));
export const AgentKeySchema = z
    .object({
    id: z.string().regex(new RegExp("^agk_[0-9A-HJKMNP-TV-Z]{26}$")),
    userId: z
        .string()
        .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
    spaceId: z
        .string()
        .regex(new RegExp("^spc_[0-9A-Za-z]{11}$"))
        .describe("A space identifier. Eleven letters and digits, no separators, because it appears in an address somebody reads and types (ADR 000033)."),
    name: z.string().min(1).max(200),
    hint: z
        .string()
        .describe("The last few characters of the secret, so two keys can be told apart in a listing without either being recoverable from it."),
    createdAt: z.string().datetime({ offset: true }),
    expiresAt: z.union([z.string().datetime({ offset: true }), z.null()]),
    lastUsedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .describe("Enough to answer whether a key is still in use before revoking it, without a request log nobody asked for."),
    revokedAt: z.union([z.string().datetime({ offset: true }), z.null()]),
})
    .strict()
    .describe("A credential for a non-human caller. The secret is not here and is never stored: only its hash is, so a database that leaks does not leak working keys (ADR 000030).");
export const NewAgentKeySchema = z
    .object({
    name: z.string().min(1).max(200),
    expiresInDays: z
        .number()
        .int()
        .gte(0)
        .lte(3650)
        .describe("Zero means it does not expire, which is right for a long-running agent and wrong for a key pasted into a laptop for an afternoon.")
        .optional(),
})
    .strict()
    .describe("What a caller sends to mint a key.");
export const AgentKeyCreatedSchema = z
    .object({
    key: z
        .object({
        id: z.string().regex(new RegExp("^agk_[0-9A-HJKMNP-TV-Z]{26}$")),
        userId: z
            .string()
            .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
            .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
        spaceId: z
            .string()
            .regex(new RegExp("^spc_[0-9A-Za-z]{11}$"))
            .describe("A space identifier. Eleven letters and digits, no separators, because it appears in an address somebody reads and types (ADR 000033)."),
        name: z.string().min(1).max(200),
        hint: z
            .string()
            .describe("The last few characters of the secret, so two keys can be told apart in a listing without either being recoverable from it."),
        createdAt: z.string().datetime({ offset: true }),
        expiresAt: z.union([z.string().datetime({ offset: true }), z.null()]),
        lastUsedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .describe("Enough to answer whether a key is still in use before revoking it, without a request log nobody asked for."),
        revokedAt: z.union([z.string().datetime({ offset: true }), z.null()]),
    })
        .strict()
        .describe("A credential for a non-human caller. The secret is not here and is never stored: only its hash is, so a database that leaks does not leak working keys (ADR 000030)."),
    secret: z
        .string()
        .regex(new RegExp("^sk_scenix_[A-Za-z0-9_-]+$"))
        .describe("The full credential. The prefix is visible on purpose: a key pasted into a chat log or a commit is findable by grepping for it."),
})
    .strict()
    .describe("The one and only time the secret exists outside the caller's hands. It is not stored and cannot be shown again.");
export const NewLogoUploadSchema = z
    .object({ contentType: z.string().max(200) })
    .strict()
    .describe("What a caller sends to start uploading a space's logo (ADR 000039).");
export const OAuthClientIdSchema = z.string().regex(new RegExp("^oac_[0-9A-HJKMNP-TV-Z]{26}$"));
export const OAuthGrantIdSchema = z.string().regex(new RegExp("^oag_[0-9A-HJKMNP-TV-Z]{26}$"));
export const OAuthGrantSchema = z
    .object({
    id: z.string().regex(new RegExp("^oag_[0-9A-HJKMNP-TV-Z]{26}$")),
    clientId: z.string().regex(new RegExp("^oac_[0-9A-HJKMNP-TV-Z]{26}$")),
    userId: z
        .string()
        .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
    spaceId: z
        .string()
        .regex(new RegExp("^spc_[0-9A-Za-z]{11}$"))
        .describe("A space identifier. Eleven letters and digits, no separators, because it appears in an address somebody reads and types (ADR 000033)."),
    clientName: z
        .string()
        .describe("Copied at consent time, so a listing says what was authorized without reading the client, and still says something useful after the client is gone."),
    createdAt: z.string().datetime({ offset: true }),
    lastUsedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .describe("Enough to answer whether a connector is still in use before somebody revokes it."),
    revokedAt: z.union([z.string().datetime({ offset: true }), z.null()]),
})
    .strict()
    .describe("One person's standing authorization of one client, in one space (ADR 000043). What a connectors page lists, and what revoke stops.");
export const SessionIdSchema = z
    .string()
    .regex(new RegExp("^ses_[0-9A-HJKMNP-TV-Z]{26}$"))
    .describe("A signed-in browser, as ses_…. Not derived from the session token: the document is keyed by that token's hash, and this is what the API hands out instead (ADR 000086).");
export const SessionSchema = z
    .object({
    id: z
        .string()
        .regex(new RegExp("^ses_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("A signed-in browser, as ses_…. Not derived from the session token: the document is keyed by that token's hash, and this is what the API hands out instead (ADR 000086)."),
    createdAt: z.string().datetime({ offset: true }),
    expiresAt: z
        .string()
        .datetime({ offset: true })
        .describe("When the session stops working on its own. Absolute rather than sliding, so it is a date rather than an idle timer."),
    current: z
        .boolean()
        .describe("True for the session the request was made with, which is the one row a person must not end by accident."),
})
    .strict()
    .describe("One place an account is signed in (ADR 000086). What the sessions panel lists, and what ending one stops. It says when, deliberately not where: nothing records the address or the browser a session was created from.");
export const MomentIdSchema = z.string().regex(new RegExp("^mmt_[0-9A-HJKMNP-TV-Z]{26}$"));
export const MomentSchema = z
    .object({
    id: z.string().regex(new RegExp("^mmt_[0-9A-HJKMNP-TV-Z]{26}$")),
    assetId: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
    range: z
        .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
        .strict()
        .describe("A half-open selection over an asset's timeline, in seconds."),
    title: z.string().min(1).max(200).describe("One line, short enough for a timeline lane."),
    body: z.string().max(10000).optional(),
    tags: z.array(z.string()).max(20).describe("Lowercased, trimmed and deduped on the way in."),
    authorKind: z.enum(["user", "assistant", "system"]),
    authorId: z.string().regex(new RegExp("^(usr_[0-9A-HJKMNP-TV-Z]{26})?$")).optional(),
    thumbnailPath: z
        .string()
        .describe("Set by the server when a still is uploaded for this moment, never by a caller.")
        .optional(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    deletedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .describe("Set when the moment is in the recovery window: the record and its still are both still here, invisible until restored (ADR 000055)."),
})
    .strict()
    .describe("A durable, authored observation about a range of one asset. Unlike everything in the manifest, it is kept and corrected rather than regenerated by the next analysis (ADR 000051).");
export const NewMomentSchema = z
    .object({
    range: z
        .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
        .strict()
        .describe("A half-open selection over an asset's timeline, in seconds."),
    title: z.string().min(1).max(200),
    body: z.string().max(10000).optional(),
    tags: z.array(z.string()).max(20).optional(),
})
    .strict()
    .describe("What a caller sends to record a moment.");
export const MomentChangeSchema = z
    .object({
    range: z
        .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
        .strict()
        .describe("A half-open selection over an asset's timeline, in seconds.")
        .optional(),
    title: z.string().min(1).max(200).optional(),
    body: z.string().max(10000).optional(),
    tags: z.array(z.string()).max(20).optional(),
})
    .strict()
    .describe("Corrects a moment. Every field is optional: absent means unchanged, so a caller can fix a title without resending a body it never read.");
export const ShortlistEntrySchema = z
    .object({
    assetId: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
    momentId: z.string().regex(new RegExp("^mmt_[0-9A-HJKMNP-TV-Z]{26}$")),
    addedBy: z
        .string()
        .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
    addedAt: z.string().datetime({ offset: true }),
    moment: z
        .object({
        id: z.string().regex(new RegExp("^mmt_[0-9A-HJKMNP-TV-Z]{26}$")),
        assetId: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
        range: z
            .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
            .strict()
            .describe("A half-open selection over an asset's timeline, in seconds."),
        title: z.string().min(1).max(200).describe("One line, short enough for a timeline lane."),
        body: z.string().max(10000).optional(),
        tags: z
            .array(z.string())
            .max(20)
            .describe("Lowercased, trimmed and deduped on the way in."),
        authorKind: z.enum(["user", "assistant", "system"]),
        authorId: z.string().regex(new RegExp("^(usr_[0-9A-HJKMNP-TV-Z]{26})?$")).optional(),
        thumbnailPath: z
            .string()
            .describe("Set by the server when a still is uploaded for this moment, never by a caller.")
            .optional(),
        createdAt: z.string().datetime({ offset: true }),
        updatedAt: z.string().datetime({ offset: true }),
        deletedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .describe("Set when the moment is in the recovery window: the record and its still are both still here, invisible until restored (ADR 000055)."),
    })
        .strict()
        .describe("Resolved on read. Absent when the moment has been deleted since: a dangling entry is filtered from the listing rather than blocking the delete.")
        .optional(),
})
    .strict()
    .describe("A moment somebody gathered for a project. It names the moment rather than copying it, so a moment corrected on the asset stays correct everywhere it was sent (ADR 000051).");
export const NewShortlistEntrySchema = z
    .object({
    assetId: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
    momentId: z.string().regex(new RegExp("^mmt_[0-9A-HJKMNP-TV-Z]{26}$")),
})
    .strict()
    .describe("What a caller sends to gather a moment for a project.");
export const ShareIdSchema = z
    .string()
    .regex(new RegExp("^shr_[0-9A-HJKMNP-TV-Z]{26}$"))
    .describe("A share, as shr_…");
export const ShareSchema = z
    .object({
    id: z.string().regex(new RegExp("^shr_[0-9A-HJKMNP-TV-Z]{26}$")).describe("A share, as shr_…"),
    spaceId: z
        .string()
        .regex(new RegExp("^spc_[0-9A-Za-z]{11}$"))
        .describe("A space identifier. Eleven letters and digits, no separators, because it appears in an address somebody reads and types (ADR 000033)."),
    projectId: z
        .string()
        .regex(new RegExp("^prj_[0-9A-Za-z]{11}$"))
        .describe("A project identifier. Eleven letters and digits, no separators (ADR 000033)."),
    label: z.string().min(1).max(120).describe("Who or what the link was made for."),
    hint: z
        .string()
        .describe("The tail of the token, for telling two links apart in a listing without either being recoverable from it."),
    createdBy: z
        .string()
        .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
        .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
    createdAt: z.string().datetime({ offset: true }),
    expiresAt: z
        .union([
        z.string().datetime({ offset: true }).describe("Null means it does not expire."),
        z.null().describe("Null means it does not expire."),
    ])
        .describe("Null means it does not expire.")
        .optional(),
    lastUsedAt: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
    revokedAt: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
})
    .strict()
    .describe("A read-only link to one project, reachable without an account. Served by its own routes rather than by widening what a membership means (ADR 000057).");
export const NewShareSchema = z
    .object({
    projectId: z
        .string()
        .regex(new RegExp("^prj_[0-9A-Za-z]{11}$"))
        .describe("A project identifier. Eleven letters and digits, no separators (ADR 000033).")
        .optional(),
    label: z.string().min(1).max(120),
    expiresInDays: z.number().int().gte(0).optional(),
})
    .strict()
    .describe("What a caller sends to mint a share.");
export const ShareCreatedSchema = z
    .object({
    share: z
        .object({
        id: z
            .string()
            .regex(new RegExp("^shr_[0-9A-HJKMNP-TV-Z]{26}$"))
            .describe("A share, as shr_…"),
        spaceId: z
            .string()
            .regex(new RegExp("^spc_[0-9A-Za-z]{11}$"))
            .describe("A space identifier. Eleven letters and digits, no separators, because it appears in an address somebody reads and types (ADR 000033)."),
        projectId: z
            .string()
            .regex(new RegExp("^prj_[0-9A-Za-z]{11}$"))
            .describe("A project identifier. Eleven letters and digits, no separators (ADR 000033)."),
        label: z.string().min(1).max(120).describe("Who or what the link was made for."),
        hint: z
            .string()
            .describe("The tail of the token, for telling two links apart in a listing without either being recoverable from it."),
        createdBy: z
            .string()
            .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
            .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
        createdAt: z.string().datetime({ offset: true }),
        expiresAt: z
            .union([
            z.string().datetime({ offset: true }).describe("Null means it does not expire."),
            z.null().describe("Null means it does not expire."),
        ])
            .describe("Null means it does not expire.")
            .optional(),
        lastUsedAt: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
        revokedAt: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
    })
        .strict()
        .describe("A read-only link to one project, reachable without an account. Served by its own routes rather than by widening what a membership means (ADR 000057)."),
    token: z
        .string()
        .describe("The full credential, including the sl_scenix_ prefix. Deliberately unlike a share's id, so a reader can tell a secret from a reference at a glance."),
})
    .strict()
    .describe("The one and only time the token exists outside the caller's hands. It is not stored and cannot be shown again.");
export const SharedTimelineSchema = z
    .object({
    durationSec: z
        .number()
        .gte(0)
        .describe("Where the chain ends: the planned length, which is what the briefs say."),
    renderedDurationSec: z
        .number()
        .gte(0)
        .describe("The length of the segments that have actually been made."),
    readyDurationSec: z
        .number()
        .gte(0)
        .describe("How much of the planned length is made. Over durationSec this is progress, which is what every surface reads off the chain rather than declaring (ADR 000067)."),
})
    .strict()
    .describe("How long the cut is and how much of it is made, for somebody holding a link. The member view's Timeline carries its ids, its version, its track count and who started it, none of which is a share holder's business (ADR 000132).");
export const SharedSegmentSchema = z
    .object({
    id: z.string().regex(new RegExp("^seg_[0-9A-HJKMNP-TV-Z]{26}$")),
    startSec: z.number().gte(0),
    endSec: z.number().gte(0),
    state: z
        .enum(["planned", "queued", "rendering", "ready", "failed", "stale"])
        .describe("Whether a segment's picture has been made. `planned` is the storyboard: there is no storyboard entity, only segments in this state (ADR 000025)."),
    label: z.string(),
    direction: z
        .string()
        .max(8000)
        .describe("What this shot is, taken from the brief. The one part of a brief written to be read by a person, and the only part a share serves: not the narration, not the sources it was cut from, not the render notes, not the layout (ADR 000132)."),
})
    .strict()
    .describe("One shot as somebody holding a link sees it: where it sits, what it is called, whether it has been made, and the sentence saying what it is. The rest of the brief is how the shot was made rather than the shot, and a share carries none of it (ADR 000132).");
export const SharedRenditionSchema = z
    .object({
    id: z.string().regex(new RegExp("^rnd_[0-9A-HJKMNP-TV-Z]{26}$")),
    segmentId: z
        .string()
        .regex(new RegExp("^(seg_[0-9A-HJKMNP-TV-Z]{26})?$"))
        .describe("A seg identifier, or empty when there is none."),
})
    .strict()
    .describe("Somewhere to play, and the shot it belongs to. A share serves only the renditions it can play, so a failed one, which carries the reason it failed, and a queued one, which says only that somebody is working, are absent rather than narrowed (ADR 000132).");
export const SharedProjectSchema = z
    .object({
    projectName: z.string(),
    spaceName: z.string(),
    timeline: z
        .object({
        durationSec: z
            .number()
            .gte(0)
            .describe("Where the chain ends: the planned length, which is what the briefs say."),
        renderedDurationSec: z
            .number()
            .gte(0)
            .describe("The length of the segments that have actually been made."),
        readyDurationSec: z
            .number()
            .gte(0)
            .describe("How much of the planned length is made. Over durationSec this is progress, which is what every surface reads off the chain rather than declaring (ADR 000067)."),
    })
        .strict()
        .describe("How long the cut is and how much of it is made, for somebody holding a link. The member view's Timeline carries its ids, its version, its track count and who started it, none of which is a share holder's business (ADR 000132)."),
    segments: z
        .array(z
        .object({
        id: z.string().regex(new RegExp("^seg_[0-9A-HJKMNP-TV-Z]{26}$")),
        startSec: z.number().gte(0),
        endSec: z.number().gte(0),
        state: z
            .enum(["planned", "queued", "rendering", "ready", "failed", "stale"])
            .describe("Whether a segment's picture has been made. `planned` is the storyboard: there is no storyboard entity, only segments in this state (ADR 000025)."),
        label: z.string(),
        direction: z
            .string()
            .max(8000)
            .describe("What this shot is, taken from the brief. The one part of a brief written to be read by a person, and the only part a share serves: not the narration, not the sources it was cut from, not the render notes, not the layout (ADR 000132)."),
    })
        .strict()
        .describe("One shot as somebody holding a link sees it: where it sits, what it is called, whether it has been made, and the sentence saying what it is. The rest of the brief is how the shot was made rather than the shot, and a share carries none of it (ADR 000132)."))
        .default([]),
    renditions: z
        .array(z
        .object({
        id: z.string().regex(new RegExp("^rnd_[0-9A-HJKMNP-TV-Z]{26}$")),
        segmentId: z
            .string()
            .regex(new RegExp("^(seg_[0-9A-HJKMNP-TV-Z]{26})?$"))
            .describe("A seg identifier, or empty when there is none."),
    })
        .strict()
        .describe("Somewhere to play, and the shot it belongs to. A share serves only the renditions it can play, so a failed one, which carries the reason it failed, and a queued one, which says only that somebody is working, are absent rather than narrowed (ADR 000132)."))
        .default([]),
    mediaUrls: z
        .record(z.string(), z.string())
        .describe("Short-lived signed URLs keyed by rendition id. A share holder never learns a storage path."),
    posterUrls: z
        .record(z.string(), z.string())
        .describe("The same, for the still each playable rendition carries."),
})
    .strict()
    .describe("Everything a share serves: the cut, and nothing else. Shapes of its own rather than the member view with fields removed, so a field added for a member does not reach a share until somebody adds it here (ADR 000057, ADR 000132).");
export const UploadTargetSchema = z
    .object({ uploadUrl: z.string(), expiresAt: z.string().datetime({ offset: true }) })
    .strict()
    .describe("Where to PUT bytes, and until when. Every endpoint that hands out a signed upload answers in this shape: a space's logo, an oversized manifest, a moment's still.");
export const RectSchema = z
    .object({
    x: z.number().gte(0).lte(1),
    y: z.number().gte(0).lte(1),
    w: z.number().gt(0).lte(1),
    h: z.number().gt(0).lte(1),
})
    .strict()
    .describe("A rectangle in normalised coordinates: every value from 0 to 1, measured against whatever contains it. Not pixels, so the same layout renders at any resolution and nesting is a multiplication (ADR 000066).");
export const LayoutFitSchema = z
    .enum(["cover", "contain"])
    .describe("What happens when a box and its content disagree about shape. cover fills the box and crops the overflow; contain shows all of it and leaves bars.");
export const LayoutContentSchema = z
    .object({
    footage: z
        .object({
        assetId: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
        range: z
            .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
            .strict()
            .describe("A half-open selection over an asset's timeline, in seconds."),
    })
        .strict()
        .describe("A piece of film: this asset, from here to there.")
        .optional(),
    still: z
        .string()
        .describe("An image, for a box holding a card rather than footage.")
        .optional(),
    component: z
        .object({
        name: z
            .string()
            .regex(new RegExp("^[a-z][a-z0-9]*(-[a-z0-9]+)*$"))
            .min(1)
            .max(64)
            .describe("Which component. Lower case words joined by dashes, narrow because the name selects code to run and ends up in a registry lookup and a path."),
        props: z
            .record(z.string(), z.any())
            .describe("Everything it draws, as data rather than a query. A component that read the thread or the corpus at render time would make the brief stop determining the picture, and the fingerprint would be a claim nobody could check (ADR 000026). At most 16KB encoded, because what it shows travels in the brief.")
            .optional(),
        renderer: z
            .enum(["satori", "canvas", "dom"])
            .describe("The engine this needs. Absent reads as satori.")
            .optional(),
    })
        .strict()
        .describe("A component drawn by the render engine rather than footage somebody shot (ADR 000069). A pure function of its props and the time within the segment: no state, no timers, no reading the clock and no fetching, because a renderer walks frames in whatever order it likes and a re-render has to match what somebody already approved.")
        .optional(),
    crop: z
        .object({
        x: z.number().gte(0).lte(1),
        y: z.number().gte(0).lte(1),
        w: z.number().gt(0).lte(1),
        h: z.number().gt(0).lte(1),
    })
        .strict()
        .describe("The part of the source used, in normalised source coordinates. Absent means all of it.")
        .optional(),
})
    .strict()
    .describe("What fills a leaf box: footage, a still, or a component. The either-or is in the shape rather than a pair of fields that are sometimes empty.");
export const LayoutBoxSchema = z.lazy(() => z
    .object({
    rect: RectSchema,
    content: LayoutContentSchema.optional(),
    boxes: z.array(LayoutBoxSchema).optional(),
    fit: LayoutFitSchema.optional(),
    muted: z.boolean().optional(),
    label: z.string().optional(),
    renderNotes: z.array(RenderNoteSchema).optional(),
})
    .strict());
export const ComponentRendererSchema = z
    .enum(["satori", "canvas", "dom"])
    .describe("The engine a component needs in order to be drawn. Named wider than what is built, the way fidelity is: a component needing a canvas or a real browser can be described and stored before anything can draw it, and asking for it is refused with the reason rather than served a picture made by something else (ADR 000069). Only satori is built.");
export const LayoutComponentSchema = z
    .object({
    name: z
        .string()
        .regex(new RegExp("^[a-z][a-z0-9]*(-[a-z0-9]+)*$"))
        .min(1)
        .max(64)
        .describe("Which component. Lower case words joined by dashes, narrow because the name selects code to run and ends up in a registry lookup and a path."),
    props: z
        .record(z.string(), z.any())
        .describe("Everything it draws, as data rather than a query. A component that read the thread or the corpus at render time would make the brief stop determining the picture, and the fingerprint would be a claim nobody could check (ADR 000026). At most 16KB encoded, because what it shows travels in the brief.")
        .optional(),
    renderer: z
        .enum(["satori", "canvas", "dom"])
        .describe("The engine this needs. Absent reads as satori.")
        .optional(),
})
    .strict()
    .describe("A component drawn by the render engine rather than footage somebody shot (ADR 000069). A pure function of its props and the time within the segment: no state, no timers, no reading the clock and no fetching, because a renderer walks frames in whatever order it likes and a re-render has to match what somebody already approved.");
export const ComponentInfoSchema = z
    .object({
    name: z
        .string()
        .regex(new RegExp("^[a-z][a-z0-9]*(-[a-z0-9]+)*$"))
        .describe("What to put in a box's component."),
    summary: z.string().describe("What this component is, for somebody choosing one."),
    props: z
        .record(z.string(), z.any())
        .describe("What it takes, as JSON Schema. Handed over as it stands, because a model given a schema cannot invent something malformed and a model given a blank object will."),
})
    .strict()
    .describe("One component a layout box can hold: what it is, and what it takes (ADR 000069). Generated from the component registry rather than written here, so the description an agent reads cannot drift from the component that has to draw it.");
export const LayoutFootageSchema = z
    .object({
    assetId: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
    range: z
        .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
        .strict()
        .describe("A half-open selection over an asset's timeline, in seconds."),
})
    .strict()
    .describe("A piece of film: this asset, from here to there.");
export const RenderNoteSchema = z
    .object({
    key: z
        .string()
        .min(1)
        .max(64)
        .describe("What to do. Understood here: fit, background, whenShort."),
    value: z
        .string()
        .max(500)
        .describe("How. A value a renderer cannot act on is reported as unapplied too: fit takes cover or contain, whenShort takes black or hold, and a background has to be a colour the renderer knows.")
        .optional(),
    why: z
        .string()
        .max(2000)
        .describe("For the person who reads this in six months and would otherwise delete it.")
        .optional(),
})
    .strict()
    .describe("One instruction addressed to whatever renders this, where direction is prose for a person and must not be interpreted by a machine. A key this renderer does not understand is carried and reported as unapplied rather than refused (ADR 000068).");
export const AnalysisStageSchema = z
    .enum(["probe", "thumbnail", "transcribe", "describe"])
    .describe("One step of the analysis pipeline a caller can name, in pipeline order, so that asking for a fresh description is a different request from asking for a fresh transcript (ADR 000063). Go is authoritative for the steps themselves; this is where the names are written down.");
export const AnalysisEngineSchema = z
    .enum(["elevenlabs", "gemini"])
    .describe("A paid service a stage cannot run without (ADR 000029). ffprobe and ffmpeg are absent because mise pins both, so no stage that uses them can be unfillable for want of a key (ADR 000129).");
export const UnfillableStageSchema = z
    .object({
    stage: z
        .enum(["probe", "thumbnail", "transcribe", "describe"])
        .describe("One step of the analysis pipeline a caller can name, in pipeline order, so that asking for a fresh description is a different request from asking for a fresh transcript (ADR 000063). Go is authoritative for the steps themselves; this is where the names are written down."),
    engine: z
        .enum(["elevenlabs", "gemini"])
        .describe("A paid service a stage cannot run without (ADR 000029). ffprobe and ffmpeg are absent because mise pins both, so no stage that uses them can be unfillable for want of a key (ADR 000129)."),
})
    .strict()
    .describe("A gap this deployment has no credential to fill, and the engine whose key is missing, so a reader is told which secret would fix it rather than that nothing happened (ADR 000129).");
export const AssetDetailSchema = z
    .object({
    asset: z
        .object({
        id: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
        spaceId: z
            .string()
            .regex(new RegExp("^spc_[0-9A-Za-z]{11}$"))
            .describe("A space identifier. Eleven letters and digits, no separators, because it appears in an address somebody reads and types (ADR 000033)."),
        kind: z
            .enum(["video", "image", "audio", "document", "link"])
            .describe("What scenix ingests. `document` is a file of text (a PDF, a transcript, notes) and is not a Document, which is the Markdown a project edits (ADR 000028)."),
        source: z
            .enum(["upload", "youtube", "link", "generated"])
            .describe("How an asset entered the system."),
        status: z
            .enum(["pending", "analyzing", "ready", "failed"])
            .describe("Where an asset is in ingest: pending -> analyzing -> ready | failed. A pending asset past its uploadExpiresAt is abandoned and cannot reach another state (ADR 000054)."),
        name: z.string().min(1).max(500),
        storagePath: z
            .string()
            .describe("Where the original bytes live. Empty until they exist: an upload has a path reserved before anything arrives, and an import has none until the worker has fetched something (ADR 000028)."),
        manifestPath: z.string().optional(),
        sourceUrl: z
            .string()
            .describe("Where the content came from. Records what the content is rather than how the bytes arrived, so an upload may have one (ADR 000060).")
            .optional(),
        failureReason: z
            .string()
            .describe("Why the last analysis pass ended without finishing. Carried by a failed asset, and also by a ready one whose re-analysis gave up: that pass leaves the manifest as it found it rather than failing a working asset, so the reason is the only record that the request was not answered (ADR 000063, ADR 000120).")
            .optional(),
        uploadExpiresAt: z
            .string()
            .datetime({ offset: true })
            .describe("When the signed URL this asset was created with stops working. Absent for anything that never had one, which is not the same as expired long ago: an import was never waiting for bytes (ADR 000054).")
            .optional(),
        createdBy: z
            .string()
            .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
            .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
        createdAt: z.string().datetime({ offset: true }),
        updatedAt: z.string().datetime({ offset: true }),
        deletedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .describe("Set when the asset is in the recovery window: the record and the bytes are both still here, invisible until restored (issue #8)."),
        importMode: z
            .enum(["", "transcribe", "audio", "full"])
            .describe("How much of the source was taken, or empty for an upload, which has no source to take part of."),
        summary: z
            .string()
            .describe("Denormalised from the manifest when analysis finishes, so that listing a space does not mean pulling a manifest per asset (ADR 000028)."),
        topics: z.array(z.string()),
        durationSec: z.number().gte(0),
        sizeBytes: z.number().int().gte(0),
        thumbnailPath: z
            .string()
            .describe("A still for the browser: a frame for video, the image itself for a still, a rendered first page for a document."),
        analysisEngine: z
            .string()
            .describe("What produced the derived detail, so a systematic error is traceable to a version and a better model means a re-run (ADR 000029)."),
        analysisAttempts: z
            .number()
            .int()
            .gte(0)
            .describe("How many times a worker has claimed this. Past a small limit it fails permanently rather than retrying forever (ADR 000032)."),
    })
        .strict()
        .describe("Belongs to a space, not a project, so one ingested master can back many projects."),
    manifest: z
        .union([
        z
            .object({
            version: z.literal(1),
            assetId: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
            kind: z
                .enum(["video", "image", "audio", "document", "link"])
                .describe("What scenix ingests. `document` is a file of text (a PDF, a transcript, notes) and is not a Document, which is the Markdown a project edits (ADR 000028)."),
            durationSec: z.number().gte(0),
            container: z.string().optional(),
            sizeBytes: z.number().int().gte(0),
            checksumSha256: z.string().regex(new RegExp("^[0-9a-f]{64}$")),
            video: z.union([
                z
                    .object({
                    codec: z.string(),
                    widthPx: z.number().int().gte(1),
                    heightPx: z.number().int().gte(1),
                    fps: z.number().gt(0),
                    bitrateBps: z.number().int().optional(),
                    keyframesSec: z
                        .array(z.number().gte(0))
                        .describe("Ascending keyframe offsets. This is what makes range extraction a stream copy."),
                })
                    .strict(),
                z.null(),
            ]),
            audio: z.union([
                z
                    .object({
                    codec: z.string(),
                    channels: z.number().int().gte(1),
                    sampleRateHz: z.number().int().gte(1),
                    bitrateBps: z.number().int().optional(),
                })
                    .strict(),
                z.null(),
            ]),
            transcript: z.union([
                z
                    .object({
                    language: z.string().min(2),
                    engine: z.string(),
                    engineVersion: z.string(),
                    segments: z.array(z
                        .object({
                        index: z.number().int().gte(0),
                        text: z.string(),
                        startSec: z.number().gte(0),
                        endSec: z.number().gte(0),
                        speaker: z.string().optional(),
                        words: z.array(z
                            .object({
                            text: z.string(),
                            startSec: z.number().gte(0),
                            endSec: z.number().gte(0),
                            confidence: z.number().gte(0).lte(1).optional(),
                        })
                            .strict()
                            .describe("Word-level timing is what lets a selection snap to a sentence and a citation point at an offset.")),
                    })
                        .strict()),
                })
                    .strict(),
                z.null(),
            ]),
            text: z
                .string()
                .describe("A document's own content. What a transcript is for spoken media, for an asset with no duration (ADR 000028, ADR 000037).")
                .optional(),
            shots: z.array(z
                .object({
                index: z.number().int().gte(0),
                startSec: z.number().gte(0),
                endSec: z.number().gte(0),
                thumbnailPath: z.string().optional(),
            })
                .strict()
                .describe("A detected shot boundary: the unit a rough cut is assembled from.")),
            summary: z.string().optional(),
            topics: z.array(z.string()),
            sourceMetadata: z
                .record(z.string(), z.any())
                .describe("Whatever the source carried that scenix does not model, kept verbatim."),
            extractedAt: z.string().datetime({ offset: true }),
            audioEvents: z.array(z
                .object({
                kind: z.enum([
                    "music_start",
                    "music_change",
                    "music_end",
                    "silence",
                    "speech_end",
                    "laughter",
                    "applause",
                    "level_change",
                ]),
                startSec: z.number().gte(0),
                endSec: z.number().gte(0),
                description: z.string(),
                level: z
                    .number()
                    .describe("Measured value where one applies, such as dBFS for a level change.")
                    .optional(),
            })
                .strict()
                .describe("A range, not an instant. This is what makes 'cut where he stops talking' answerable (ADR 000024).")),
            visuals: z.array(z
                .object({
                startSec: z.number().gte(0),
                endSec: z.number().gte(0),
                description: z
                    .string()
                    .describe("What is on screen, in prose: who, doing what, framed how, where."),
                onScreenText: z.string().optional(),
            })
                .strict()
                .describe("What makes 'find the harbour at dusk' a text search.")),
            energy: z.array(z
                .object({
                atSec: z.number().gte(0),
                audio: z.number().gte(0).lte(1),
                visual: z.number().gte(0).lte(1),
            })
                .strict()
                .describe("A coarse intensity curve, so 'it drags in the middle' is checkable.")),
            thumbnails: z
                .array(z
                .object({ atSec: z.number().gte(0), path: z.string() })
                .strict()
                .describe("A still pulled from a video at a known moment. Comes from ffprobe duration alone, so it exists for every video regardless of which vision engine is configured (issue #3)."))
                .describe("Stills pulled from the video at a spread of moments, so its own page can show more than the cover frame (issue #3)."),
            analysisEngine: z
                .string()
                .describe("Which engine produced the derived detail, so a systematic error is traceable to a version.")
                .optional(),
        })
            .strict()
            .describe("The sidecar: everything extracted about an asset, in one document designed to be handed to a model whole."),
        z.null(),
    ])
        .describe("Null until analysis has written one, and null again when the sidecar cannot be read: the record is still worth showing without it (ADR 000009)."),
    moments: z
        .array(z
        .object({
        id: z.string().regex(new RegExp("^mmt_[0-9A-HJKMNP-TV-Z]{26}$")),
        assetId: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
        range: z
            .object({ startSec: z.number().gte(0), endSec: z.number().gte(0) })
            .strict()
            .describe("A half-open selection over an asset's timeline, in seconds."),
        title: z
            .string()
            .min(1)
            .max(200)
            .describe("One line, short enough for a timeline lane."),
        body: z.string().max(10000).optional(),
        tags: z
            .array(z.string())
            .max(20)
            .describe("Lowercased, trimmed and deduped on the way in."),
        authorKind: z.enum(["user", "assistant", "system"]),
        authorId: z.string().regex(new RegExp("^(usr_[0-9A-HJKMNP-TV-Z]{26})?$")).optional(),
        thumbnailPath: z
            .string()
            .describe("Set by the server when a still is uploaded for this moment, never by a caller.")
            .optional(),
        createdAt: z.string().datetime({ offset: true }),
        updatedAt: z.string().datetime({ offset: true }),
        deletedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .describe("Set when the moment is in the recovery window: the record and its still are both still here, invisible until restored (ADR 000055)."),
    })
        .strict()
        .describe("A durable, authored observation about a range of one asset. Unlike everything in the manifest, it is kept and corrected rather than regenerated by the next analysis (ADR 000051)."))
        .describe("Carried with the asset rather than needing a second call, so a reader sees what people concluded next to the transcript (ADR 000051)."),
    mediaUrl: z
        .string()
        .describe("Short-lived and signed. Empty when there are no bytes yet, or when signing failed."),
    thumbnailUrl: z.string(),
    incompleteStages: z
        .array(z
        .enum(["probe", "thumbnail", "transcribe", "describe"])
        .describe("One step of the analysis pipeline a caller can name, in pipeline order, so that asking for a fresh description is a different request from asking for a fresh transcript (ADR 000063). Go is authoritative for the steps themselves; this is where the names are written down."))
        .describe("The stages that apply to this asset and have not produced what they fill, so an asset with a hole in it is distinguishable from one that was analysed completely. Derived from the manifest in this same response rather than stored, and empty when there is no manifest to read (ADR 000073)."),
    unfillableStages: z
        .array(z
        .object({
        stage: z
            .enum(["probe", "thumbnail", "transcribe", "describe"])
            .describe("One step of the analysis pipeline a caller can name, in pipeline order, so that asking for a fresh description is a different request from asking for a fresh transcript (ADR 000063). Go is authoritative for the steps themselves; this is where the names are written down."),
        engine: z
            .enum(["elevenlabs", "gemini"])
            .describe("A paid service a stage cannot run without (ADR 000029). ffprobe and ffmpeg are absent because mise pins both, so no stage that uses them can be unfillable for want of a key (ADR 000129)."),
    })
        .strict()
        .describe("A gap this deployment has no credential to fill, and the engine whose key is missing, so a reader is told which secret would fix it rather than that nothing happened (ADR 000129)."))
        .describe("The stages that apply to this asset and need an engine this deployment holds no key for, so a gap asking again could never fill is told from one it would, and a full stage nothing here could produce again is told from one it could. Derived from the asset's kind, so it is answered whether or not analysis has written a manifest (ADR 000129)."),
})
    .strict()
    .describe("One asset with everything its page needs, as GET /v1/spaces/{id}/assets/{id} answers: the record, its manifest once analysis has produced one, and short-lived URLs for the media and its thumbnail.");
export const AssetPageSchema = z
    .object({
    assets: z.array(z
        .object({
        id: z.string().regex(new RegExp("^ast_[0-9A-HJKMNP-TV-Z]{26}$")),
        spaceId: z
            .string()
            .regex(new RegExp("^spc_[0-9A-Za-z]{11}$"))
            .describe("A space identifier. Eleven letters and digits, no separators, because it appears in an address somebody reads and types (ADR 000033)."),
        kind: z
            .enum(["video", "image", "audio", "document", "link"])
            .describe("What scenix ingests. `document` is a file of text (a PDF, a transcript, notes) and is not a Document, which is the Markdown a project edits (ADR 000028)."),
        source: z
            .enum(["upload", "youtube", "link", "generated"])
            .describe("How an asset entered the system."),
        status: z
            .enum(["pending", "analyzing", "ready", "failed"])
            .describe("Where an asset is in ingest: pending -> analyzing -> ready | failed. A pending asset past its uploadExpiresAt is abandoned and cannot reach another state (ADR 000054)."),
        name: z.string().min(1).max(500),
        storagePath: z
            .string()
            .describe("Where the original bytes live. Empty until they exist: an upload has a path reserved before anything arrives, and an import has none until the worker has fetched something (ADR 000028)."),
        manifestPath: z.string().optional(),
        sourceUrl: z
            .string()
            .describe("Where the content came from. Records what the content is rather than how the bytes arrived, so an upload may have one (ADR 000060).")
            .optional(),
        failureReason: z
            .string()
            .describe("Why the last analysis pass ended without finishing. Carried by a failed asset, and also by a ready one whose re-analysis gave up: that pass leaves the manifest as it found it rather than failing a working asset, so the reason is the only record that the request was not answered (ADR 000063, ADR 000120).")
            .optional(),
        uploadExpiresAt: z
            .string()
            .datetime({ offset: true })
            .describe("When the signed URL this asset was created with stops working. Absent for anything that never had one, which is not the same as expired long ago: an import was never waiting for bytes (ADR 000054).")
            .optional(),
        createdBy: z
            .string()
            .regex(new RegExp("^usr_[0-9A-HJKMNP-TV-Z]{26}$"))
            .describe("Prefixed, time-ordered ULID. Crockford base32 excludes I, L, O and U."),
        createdAt: z.string().datetime({ offset: true }),
        updatedAt: z.string().datetime({ offset: true }),
        deletedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .describe("Set when the asset is in the recovery window: the record and the bytes are both still here, invisible until restored (issue #8)."),
        importMode: z
            .enum(["", "transcribe", "audio", "full"])
            .describe("How much of the source was taken, or empty for an upload, which has no source to take part of."),
        summary: z
            .string()
            .describe("Denormalised from the manifest when analysis finishes, so that listing a space does not mean pulling a manifest per asset (ADR 000028)."),
        topics: z.array(z.string()),
        durationSec: z.number().gte(0),
        sizeBytes: z.number().int().gte(0),
        thumbnailPath: z
            .string()
            .describe("A still for the browser: a frame for video, the image itself for a still, a rendered first page for a document."),
        analysisEngine: z
            .string()
            .describe("What produced the derived detail, so a systematic error is traceable to a version and a better model means a re-run (ADR 000029)."),
        analysisAttempts: z
            .number()
            .int()
            .gte(0)
            .describe("How many times a worker has claimed this. Past a small limit it fails permanently rather than retrying forever (ADR 000032)."),
    })
        .strict()
        .describe("Belongs to a space, not a project, so one ingested master can back many projects.")),
    nextCursor: z
        .string()
        .regex(new RegExp("^(ast_[0-9A-HJKMNP-TV-Z]{26})?$"))
        .describe("The asset this page stopped at, to be handed back as ?after= for the next one. Empty when the listing is complete."),
})
    .strict()
    .describe("One page of a space's corpus, as GET /v1/spaces/{id}/assets answers. Newest first, and never a manifest (ADR 000028). A listing is capped, so the page says whether anything is behind it rather than leaving a caller to infer it from a full page (ADR 000093).");
export const ComponentsResponseSchema = z
    .object({
    components: z.array(z
        .object({
        name: z
            .string()
            .regex(new RegExp("^[a-z][a-z0-9]*(-[a-z0-9]+)*$"))
            .describe("What to put in a box's component."),
        summary: z.string().describe("What this component is, for somebody choosing one."),
        props: z
            .record(z.string(), z.any())
            .describe("What it takes, as JSON Schema. Handed over as it stands, because a model given a schema cannot invent something malformed and a model given a blank object will."),
    })
        .strict()
        .describe("One component a layout box can hold: what it is, and what it takes (ADR 000069). Generated from the component registry rather than written here, so the description an agent reads cannot drift from the component that has to draw it.")),
})
    .strict()
    .describe("What a layout box may hold besides footage, as GET /v1/spaces/{id}/components answers. The same answer for everybody: a component is code this build ships rather than anything belonging to a space (ADR 000069).");
export const HealthResponseSchema = z
    .object({
    status: z.literal("ok"),
    version: z
        .string()
        .describe("The build this process is, so a deploy can be told apart from the one before it."),
})
    .strict()
    .describe("The liveness probe's answer. It reports that the process is up and serving and deliberately nothing else, so a dependency blipping cannot take down a service that was about to recover on its own.");
//# sourceMappingURL=schemas.js.map